import { nomFichierDocument, nomFichierResume } from "../../Utils/documentNaming";
import ExpressAPI, { lireMutuelleDossier } from "../../Utils/ExpressAPI";
// La lecture de la carte de mutuelle est desormais faite par le back-office
// (01/10) : la regle « faut-il lancer la lecture ? » vit dans Utils, ou elle
// se teste sans React.
import { fautLancerLecture, etatDepuisReponse, dossiersAMettreEnFile } from "../../Utils/lectureMutuelle";
// Les chips de la mutuelle (01/10 au soir) : la regle de ce qu'elles montrent
// et de ce qu'elles copient vit dans Utils, ou elle se teste sans React.
// « Format inattendu » : la regle, ses seuils et son jumeau mobile vivent
// dans Utils/formatValeurs.
import { messageFormat } from "../../Utils/formatValeurs";
import { texteACopier, messageCopie } from "../../Utils/copieCommentaire";
// La conversion d'un document en PDF, et pourquoi elle se fait ici plutot
// que sur un backend partage avec la production : voir Utils/documentPdf.
import { documentEnPdf } from "../../Utils/documentPdf";
import {
  STATUT_NOUVEAU,
  STATUT_TRAITE,
  STATUT_A_VERIFIER,
  statutDossier,
  champsPourStatut,
  nomLisible,
  libelleStatut,
} from "../../Utils/statutDossier";
import { installerPoseurQuestion } from "../../Utils/confirmationNavigation";
// Les documents d'un rendez-vous (ordo 2/3/x sans doublon, Carte Vitale) et
// l'heure de prise en charge vivent dans Utils/documentsDossier.
import {
  documentsDepuisRendezVous,
  documentsAffiches,
  heurePriseEnCharge,
} from "../../Utils/documentsDossier";
// Le tri de la colonne gauche : par heure, par salle, ou le filtre Mode direct.
import { trierDossiers, nomLieu } from "../../Utils/triDossiers";

// Les documents annonces sur la tuile d'un dossier, dans la liste de gauche.
// Libelles courts : la tuile est etroite, et la secretaire lit une silhouette
// plus qu'un mot. L'ordre suit celui du dossier.
const DOCS_TUILE = [
  { key: "prescription", court: "Ordo" },
  { key: "prescriptiontwo", court: "Ordo 2" },
  { key: "prescriptionthree", court: "Ordo 3" },
  // 14/09 : « 4eme ordo » et « consentement », photographies a la tablette.
  { key: "prescriptionfour", court: "Ordo 4" },
  { key: "mutual", court: "Mutuelle" },
  { key: "IDCard", court: "CNI" },
  { key: "vitalCard", court: "Vitale" },
  { key: "consent", court: "Consent." },
  { key: "document", court: "Doc" },
];
import { Prompt } from "react-router-dom";
import VerificationDossier from "./VerificationDossier";
import ChoixIdentite from "../Elements/ChoixIdentite";
import CarteMutuelle from "../Elements/CarteMutuelle";
import CodeLuINS from "../CodeLuINS";
import Filet from "../Elements/Filet";
import { titreSourceCode, classeSourceCode } from "../../Utils/sourceCode";
import React, { useState, useEffect, useRef, useCallback } from "react";
import JsBarcode from "jsbarcode";
import { useFirebase } from "../../Firebase/FirebaseContext";
import {
  arrayUnion,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { subDays } from "date-fns";
import moment from "moment";
// Bloc « Nouveauté » en bas de page — idée de Nicolas du 03/09.
import { BlocNouveaute } from "../Elements/Nouveaute";

// ─── Helpers ───
function nirDigits(nir) {
  return String(nir || "").replace(/\D/g, "");
}
function nirSansCle(nir) {
  const d = nirDigits(nir);
  if (d.length >= 15) return d.slice(0, 13);
  if (d.length === 13) return d;
  if (d.length > 13) return d.slice(0, 13);
  return d;
}
function nirAvecCle(nir) {
  const d = nirDigits(nir);
  if (d.length >= 15) return d.slice(0, 15);
  if (d.length === 13) return d;
  if (d.length > 15) return d.slice(0, 15);
  return d;
}

function tubeNameToColor(tubeName) {
  const lower = (tubeName || "").toLowerCase();
  if (lower.includes("violet") || lower.includes("edta")) return "violet";
  if (lower.includes("vert") || lower.includes("heparine") || lower.includes("lithium")) return "vert";
  if (lower.includes("jaune") || lower.includes("sec") || lower.includes("gel")) return "jaune";
  if (lower.includes("bleu") || lower.includes("citrate")) return "bleu";
  return "gris";
}

function buildIdentitySil(idn) {
  const nir15 = nirAvecCle(idn.nir || "");
  const nir13 = nirSansCle(idn.nir || "");
  return [
    `NOM_NAISS=${idn.lastNameBirth || ""}`,
    `NOM_USAGE=${idn.lastNameUsed || ""}`,
    `PRENOMS=${idn.firstNamesUsed || ""}`,
    `DDN=${idn.dob || ""}`,
    `SEXE=${idn.sex || ""}`,
    `NIR=${nir15 || ""}`,
    `NIR_SANS_CLE=${nir13 || ""}`,
    `INS=${idn.ins || ""}`,
    `LIEU_NAIS=${idn.birthCityCode || ""}`,
    `TEL=${idn.phone || ""}`,
  ].join("; ");
}

function buildIdentityStructured(idn) {
  return JSON.stringify({
    lastNameBirth: idn.lastNameBirth || "",
    lastNameUsed: idn.lastNameUsed || "",
    firstNamesUsed: idn.firstNamesUsed || "",
    firstNamesBirth: idn.firstNamesBirth || "",
    dob: idn.dob || "",
    sex: idn.sex || "",
    nir15: nirAvecCle(idn.nir || ""),
    nir13: nirSansCle(idn.nir || ""),
    ins: idn.ins || "",
    birthCityCode: idn.birthCityCode || "",
    phone: idn.phone || "",
  });
}

function transformAppointmentToViewData(appointment, roomName) {
  const ar = appointment.analysisResult || {};
  const patientInfo = ar.patientInfo || {};
  const prescripteurInfo = ar.prescripteurInfo || {};

  // « Prise en charge » : c'est l'heure a laquelle le patient est entre, pas
  // celle de la creation du dossier. Les rendez-vous anciens, qui ne portent
  // pas `priseEnChargeDebut`, gardent leur `created_at` (voir documentsDossier).
  const debutPrise = heurePriseEnCharge(appointment);
  const createdAt = debutPrise?.seconds
    ? moment.unix(debutPrise.seconds).format("YYYY-MM-DD HH:mm")
    : "";

  // Lieu de naissance : d'abord depuis l'IA, sinon extrait du NIR (positions 5-9 du NIR 13 chiffres)
  const nirFor13 = nirSansCle(appointment.vital || patientInfo.numeroSecuriteSociale || "");
  const birthFromNir = nirFor13.length >= 13 ? nirFor13.substring(5, 10) : "";
  const birthCityCode = patientInfo.lieuNaissance || birthFromNir;

  const identity = {
    lastNameBirth: appointment.lastName || patientInfo.nom || "",
    lastNameUsed: appointment.maritalName || "",
    firstNamesUsed: appointment.name || patientInfo.prenom || "",
    // Retour du 02/09 : la tablette n'affiche que le premier prenom, mais
    // « garde en memoire les prenoms de naissance qui eux seront affiches aux
    // secretaires ». Ils arrivent dans `firstNamesBirth` ; le repli sur le
    // prenom affiche sert aux rendez-vous anterieurs, qui ne le portent pas.
    firstNamesBirth:
      appointment.firstNamesBirth || appointment.name || patientInfo.prenom || "",
    barcode: appointment.barcode || "",
    dob: appointment.birthday || patientInfo.dateNaissance || "",
    sex: appointment.sex || patientInfo.sexe || "",
    nir: appointment.vital || patientInfo.numeroSecuriteSociale || "",
    birthCityCode,
    ins: nirFor13,
    phone: appointment.phone || "",
    email: appointment.email || "",
  };

  // Sources de chaque champ identité.
  //
  // Retour du 01/09 : « il dit que la date de naissance provient du Data Matrix
  // alors que non, c'est bien moi qui l'ai saisie à la main ». La provenance
  // n'était pas lue, elle était DEVINÉE ici : dès qu'un NIR était présent, tout
  // le bloc identité était étiqueté « Data Matrix », y compris les champs
  // saisis au clavier et ceux que le code ne porte même pas.
  //
  // La tablette enregistre désormais la provenance champ par champ, au moment
  // où elle se produit (`identitySources` sur le rendez-vous). On la LIT.
  const sourcesEnregistrees = appointment.identitySources || null;

  // Repli pour les rendez-vous antérieurs, qui ne portent pas cette provenance :
  // on ne l'invente pas. Le NIR est le seul champ dont on puisse affirmer
  // l'origine — sur tablette il ne se remplit qu'au scan. Pour tout le reste on
  // dit « Manuel », qui est le cas nominal à l'accueil. Une étiquette de source
  // fausse coûte plus cher qu'une étiquette prudente : c'est elle qui dit à la
  // secrétaire ce qu'elle peut croire sans revérifier.
  const sourcesDeduites = {
    nir: appointment.vital ? "Data Matrix" : (patientInfo.numeroSecuriteSociale ? "IA/OCR" : ""),
    lastNameBirth: appointment.lastName ? "Manuel" : (patientInfo.nom ? "IA/OCR" : ""),
    lastNameUsed: appointment.maritalName ? "Manuel" : "",
    firstNamesUsed: appointment.name ? "Manuel" : (patientInfo.prenom ? "IA/OCR" : ""),
    firstNamesBirth: appointment.firstNamesBirth ? "Data Matrix" : (appointment.name ? "Manuel" : ""),
    // Le numero de dossier a sa propre provenance : c'est le seul champ qui
    // puisse venir d'un lecteur de code-barres sans venir du Datamatrix.
    // Retour du 02/09 : « afficher si on l'a recupere par Scan ou s'il a ete
    // renseigne a la main ». Faute de mieux sur les anciens rendez-vous, on ne
    // dit rien plutot que d'affirmer un scan qui n'a peut-etre pas eu lieu.
    barcode: "",
    dob: appointment.birthday ? "Manuel" : (patientInfo.dateNaissance ? "IA/OCR" : ""),
    sex: appointment.sex ? "Manuel" : (patientInfo.sexe ? "IA/OCR" : ""),
    phone: appointment.phone ? "Manuel" : "",
    email: appointment.email ? "Manuel" : "",
  };

  const identitySources = {
    ...(sourcesEnregistrees || sourcesDeduites),
    // Ces deux-là ne viennent d'aucune saisie : ils sont dérivés du NIR ici
    // même, donc leur origine se calcule ici et pas sur la tablette.
    ins: nirFor13 ? "NIR 13 chiffres" : "",
    birthCityCode: patientInfo.lieuNaissance ? "IA/OCR" : (birthFromNir ? "NIR" : ""),
  };

  // Les documents du rendez-vous, dans l'ordre du dossier. La liste vit dans
  // Utils/documentsDossier : `iaPrescription` et `prescription` portaient tous
  // deux le meme libelle « Ordonnance » (deux pastilles pour une seule photo,
  // Nicolas du 11/09), les ordonnances suivantes remplissent Ordo 2, 3, 4…, et
  // la Carte Vitale photographiee a la tablette manquait ici.
  const documents = documentsAffiches(documentsDepuisRendezVous(appointment), 3);

  const analyses = (ar.detectedAnalyses || [])
    .filter((a) => !a.barree)
    .map((a, i) => ({
      id: `a-${i}`,
      code: a.codeSIL || a.codeNABM || `A${i + 1}`,
      codeNABM: a.codeNABM || "",
      codeSIL: a.codeSIL || "",
      label: a.analyseCorrespondante || a.analyseDetectee || "",
      tubeRecommande: a.tubeRecommande || "",
      nombreTubes: a.nombreTubes || 0,
      aJeun: !!a.aJeun,
      nonRembourse: !!a.nonRembourse,
      prixPatient: a.prixPatient || 0,
      verifiedBy: a.verifiedBy || "none",
      verifiedAt: a.verifiedAt || "",
      verifiedUser: a.verifiedUser || "",
    }));

  const tubesPlanned = (ar.tubesWithCount || []).map((t) => ({
    color: tubeNameToColor(t.nom),
    label: t.nom,
    count: t.nombreTotal || 1,
  }));

  // Tubes reconnus depuis photo (si disponible)
  // Nouveau format: { confirmedTubes: [{ nom, count }], photoPath, confirmedAt, confirmedBy }
  // Ancien format: array of { nom, count, ... }
  const scanResult = appointment.tubesScanResult;
  const rawTubesReconnus = scanResult?.confirmedTubes
    ? scanResult.confirmedTubes // array of { nom, count } or strings
    : Array.isArray(scanResult) ? scanResult
    : appointment.tubesFromPhoto || null;
  const tubesReconnus = rawTubesReconnus
    ? rawTubesReconnus.map((t) => ({
        color: tubeNameToColor(typeof t === "string" ? t : (t.nom || t.label || t.color || "")),
        label: typeof t === "string" ? t : (t.nom || t.label || t.color || ""),
        count: (typeof t === "object" ? (t.count || t.nombre || t.nombreTotal) : null) || 1,
      }))
    : null;
  const tubesScanMeta = scanResult && !Array.isArray(scanResult)
    ? { confirmedBy: scanResult.confirmedBy, confirmedAt: scanResult.confirmedAt, photoPath: scanResult.photoPath }
    : null;

  // Cohérence identité : Patient/Data Matrix vs IA/OCR ordonnance
  const hasPatientData = !!(appointment.lastName || appointment.name || appointment.birthday);
  const hasAIData = !!(patientInfo.nom || patientInfo.prenom || patientInfo.dateNaissance);
  function normalizeText(t) {
    return (t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "").trim();
  }
  function normalizeDob(d) { return (d || "").replace(/\D/g, ""); }
  let identityCoherence = null;
  if (hasPatientData && hasAIData) {
    identityCoherence = {
      nom: { a: appointment.lastName || "", b: patientInfo.nom || "", match: !appointment.lastName || !patientInfo.nom || normalizeText(appointment.lastName) === normalizeText(patientInfo.nom) },
      prenom: { a: appointment.name || "", b: patientInfo.prenom || "", match: !appointment.name || !patientInfo.prenom || normalizeText(appointment.name) === normalizeText(patientInfo.prenom) },
      dob: { a: appointment.birthday || "", b: patientInfo.dateNaissance || "", match: !appointment.birthday || !patientInfo.dateNaissance || normalizeDob(appointment.birthday) === normalizeDob(patientInfo.dateNaissance) },
      nir: { a: nirSansCle(appointment.vital || ""), b: nirSansCle(patientInfo.numeroSecuriteSociale || ""), match: !appointment.vital || !patientInfo.numeroSecuriteSociale || nirSansCle(appointment.vital) === nirSansCle(patientInfo.numeroSecuriteSociale) },
    };
    identityCoherence.isCoherent = identityCoherence.nom.match && identityCoherence.prenom.match && identityCoherence.dob.match && identityCoherence.nir.match;
  }

  // Meme date que dans « Dernier RDV » : celle du passage du preleveur, sinon
  // le jour courant. Le nom du fichier doit etre identique d un ecran a l autre.
  const jourDocument = appointment.technician_came_at?.seconds
    ? moment.unix(appointment.technician_came_at.seconds).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");

  return {
    id: appointment.id,
    dossierBarcode: appointment.barcode || ar.numeroDossier || "",
    jourDocument,
    room: roomName || "",
    createdAt,
    // La prise en charge directe (sans rendez-vous) est marquee sur le
    // rendez-vous par la tablette : l'ecran la signale en orange.
    modeDirect: !!appointment.modeDirect,
    collector: appointment.created_by || "",
    commentary: appointment.commentary || "",
    identity,
    identitySources,
    // Lecture brute du Datamatrix, enregistree par la tablette au moment du
    // scan. Nulle quand le rendez-vous n'en a pas eu : le bouton disparait.
    insReading: appointment.insReading || null,
    identityCoherence,
    tubes: tubesPlanned,
    tubesReconnus,
    tubesScanMeta,
    documents,
    analyses,
    prescripteurInfo,
    facturation: ar.facturation || null,
    confidence: ar.confidence != null ? ar.confidence : null,
    typeOrdonnance: ar.typeOrdonnance || null,
    hasAnalysisResult: !!appointment.analysisResult,
    // La carte de mutuelle lue (`mutuelleResume`), telle que le serveur l'a
    // mise au propre. Nulle sur les dossiers d'avant : la carte disparait.
    mutuelleResume: appointment.mutuelleResume || null,
    /*
      Section 5.3 : ce que l'analyse a pu dire de la QUALITE de la prise de vue.

      Elle etait calculee cote serveur depuis le 14/08 et n'etait affichee
      nulle part -- ni sur la tablette, ni ici. Un controle que personne ne voit
      n'est pas un controle. C'est cette moitie-la qui manquait, autant que la
      mesure elle-meme.
    */
    qualiteDocument: ar.qualite || null,
    mesuresImage: ar.mesuresImage || null,
  };
}

// ─── CSS ───
const CSS = `
.sb-root {
  --bg:#f2f6fb;
  --blue:#0b8fd4;
  --blue2:#0a7fbe;
  --text:#0b1b2a;
  --muted:#5f7a90;
  --card:#ffffff;
  --line:#e3ebf3;
  --ok:#1fbf6a;
  --warn:#f2a933;
  --shadow:0 2px 12px rgba(10,40,80,0.05);
  --r14:14px;
  --r18:16px;
  --tube-violet:#7a3ef2;
  --tube-vert:#1bb570;
  --tube-jaune:#f2b600;
  --tube-bleu:#3b82f6;
  --tube-gris:#9aa6b2;
  --ease: cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.sb-root *{ box-sizing:border-box; font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial; }
.sb-root {
  background:var(--bg);
  color:var(--text);
  height: calc(100vh - 64px);
  overflow:hidden;
  margin: -24px;
  width: calc(100% + 48px);
}
@media (min-width: 960px){
  .sb-root { margin: -32px; width: calc(100% + 64px); }
}
.sb-root ::-webkit-scrollbar{ width:4px; }
.sb-root ::-webkit-scrollbar-track{ background:transparent; }
.sb-root ::-webkit-scrollbar-thumb{ background:rgba(11,143,212,0.18); border-radius:99px; }

/* Layout */
.sb-layout{ display:flex; height:100%; }

/* Sidebar */
.sb-sidebar{
  width:280px;
  min-width:280px;
  background:var(--card);
  border-right:1px solid var(--line);
  display:flex;
  flex-direction:column;
  overflow:hidden;
}
.sb-sidebarHeader{
  padding:10px;
  border-bottom:1px solid var(--line);
  flex-shrink:0;
}
.sb-sidebarTitle{
  font-weight:800;
  font-size:14px;
  margin-bottom:8px;
  display:flex;
  align-items:center;
  gap:6px;
}
.sb-sidebarTitle .sb-icon{
  width:24px; height:24px;
  border-radius:8px;
  background:linear-gradient(135deg, var(--blue), var(--blue2));
  display:flex; align-items:center; justify-content:center;
  color:#fff;
  font-size:12px;
  flex:0 0 auto;
}
.sb-sidebarSearch{
  border:1px solid var(--line);
  border-radius:10px;
  padding:7px 8px;
  font-weight:600;
  background:#fbfdff;
  width:100%;
  font-size:12px;
}
.sb-sidebarSearch:focus{
  outline:none;
  border-color:var(--blue);
  box-shadow:0 0 0 2px rgba(11,143,212,0.1);
}
.sb-sidebarStats{
  display:flex; gap:4px; margin-top:6px; flex-wrap:wrap;
}
.sb-sidebarList{
  flex:1;
  overflow-y:auto;
  padding:6px;
}
.sb-apptItem{
  border:1px solid var(--line);
  border-radius:10px;
  padding:8px 10px;
  margin-bottom:4px;
  cursor:pointer;
  transition:all 0.15s var(--ease);
  background:#fff;
}
.sb-apptItem:hover{ border-color:rgba(11,143,212,0.3); background:#f8fbff; }
.sb-apptItem.sb-selected{ outline:2px solid var(--blue); background:#f0f7ff; }
.sb-apptItem.sb-seen{ border-left:3px solid var(--ok); }
.sb-apptName{ font-weight:800; font-size:12px; }
.sb-apptMeta{ font-size:10px; color:var(--muted); margin-top:2px; }
.sb-apptBadges{ display:flex; gap:3px; margin-top:4px; flex-wrap:wrap; }

/* Main content */
.sb-mainContent{ flex:1; min-width:0; overflow-y:auto; }
.sb-app{ }

/* Empty select */
.sb-emptySelect{
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  flex:1; color:var(--muted); text-align:center; gap:10px; padding:40px;
}
.sb-emptyIcon{
  width:64px; height:64px; border-radius:16px;
  background:#eaf4fb;
  display:flex; align-items:center; justify-content:center;
  font-size:28px;
}
.sb-emptyTitle{ font-weight:800; font-size:16px; color:var(--text); }
.sb-emptySubtitle{ font-size:12px; }

/* Header */
.sb-header{
  background: linear-gradient(135deg, var(--blue), var(--blue2));
  color:#fff;
  padding:10px 12px 8px;
  flex-shrink:0;
  transition: background 0.25s var(--ease);
}
/* Retour du 04/09 (17h41) : « quand le dossier est traité on pourrait mettre
   le cadre en haut en vert et quand il doit être vérifié en violet, comme ça
   on voit très vite le statut ». Le bandeau porte le nom du patient : c'est
   la premiere chose que l'oeil trouve en ouvrant un dossier, donc le bon
   endroit pour dire ou en est ce dossier. */
.sb-header.sb-hTraite{ background: linear-gradient(135deg, #0f9d58, #0b7a43); }
.sb-header.sb-hAVerifier{ background: linear-gradient(135deg, #7C3AED, #5b21b6); }
.sb-hTop{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  align-items:flex-end;
  justify-content:space-between;
}
.sb-hTitle{ font-weight:800; font-size:22px; line-height:1.2; }
.sb-hSub{ margin-top:6px; display:flex; flex-wrap:wrap; gap:4px; opacity:0.95; }

.sb-chip{
  display:inline-flex; gap:5px; align-items:center;
  padding:4px 8px; border-radius:999px;
  background: rgba(255,255,255,0.15);
  font-weight:700; white-space:nowrap;
  font-size:11px;
  border:1px solid rgba(255,255,255,0.2);
  cursor:pointer;
  user-select:none;
  color:#fff;
  transition: background 0.2s var(--ease);
}
.sb-chip:hover{ background: rgba(255,255,255,0.25); }
.sb-chip .sb-dot{ width:5px; height:5px; border-radius:99px; background:#fff; opacity:0.8; }
/* LE MODE DIRECT PASSE EN ORANGE — LE VRAI, PAS LE ROUGE BRIQUE.

   Nicolas, 19/09 (9h28) : « La couleur du mode direct ne permet pas bien de
   voir la difference avec les dossiers traites. Essaye peut etre orange. »
   Puis, a 16h15, sur mon premier essai : « Je n'aime pas ce rouge que tu as
   mis pour les mode Direct. Essaye plutot Orange. »

   J'avais pris #C2410C, qui est l'orange le plus sombre de la palette et qui
   se lit comme un rouge brique — la meme couleur que celle des alertes, ce
   qui n'aide pas. #EA580C est franchement orange et ne peut etre confondu ni
   avec le vert de « traite », ni avec le violet de « a verifier ». */
.sb-chip.sb-chipDirect{
  background:#EA580C;
  border-color:#EA580C;
  font-size:12px;
  font-weight:800;
  padding:5px 10px;
}
.sb-pill.sb-direct{
  background:#EA580C;
  border-color:#EA580C;
  color:#fff;
  font-size:10px;
  padding:3px 8px;
  font-weight:800;
}
.sb-apptItem.sb-directItem{
  border-left:4px solid #EA580C;
  background:#fff7ed;
}
/* ET LE LISERE REPASSE AU VERT QUAND LE DOSSIER EST CREE.

   J'avais fait l'inverse le 18/09 — garder l'orange pour que le dossier reste
   reconnaissable comme Mode direct. Nicolas, 19/09 (16h15), tranche
   autrement : « quand ils sont crees passe le lisere egalement en vert sinon
   on a du mal a comprendre ou on en est. »

   Il a raison sur l'usage, et mon raisonnement portait sur la mauvaise
   question. Ce qu'on cherche dans cette liste n'est pas « quel type de
   dossier est-ce », c'est « lesquels me restent a faire ». La pastille
   « Mode direct » dit deja le type ; le lisere doit dire l'avancement, et
   rien d'autre. */
.sb-apptItem.sb-directItem.sb-seen{
  border-left-color: var(--ok);
  background:#f3fbf6;
}
/* Les trois pastilles de tri de la liste des dossiers (Nicolas, 13/09). */
.sb-sortRow{ display:flex; gap:4px; margin-top:6px; flex-wrap:wrap; }
.sb-sortBtn{
  border:1px solid var(--line);
  background:#fff;
  color:var(--muted);
  border-radius:999px;
  padding:3px 8px;
  font-size:10px;
  font-weight:800;
  cursor:pointer;
}
.sb-sortBtn.sb-on{ background:#0b8fd4; border-color:#0b8fd4; color:#fff; }

/* Content grid */
.sb-content{
  padding:8px;
  display:grid;
  grid-template-columns: minmax(260px, 1fr) minmax(320px, 2fr);
  gap:8px;
}
.sb-col{ display:flex; flex-direction:column; gap:8px; }

/* Cards */
.sb-card{
  background:var(--card);
  border:1px solid var(--line);
  border-radius: var(--r18);
  box-shadow: var(--shadow);
  padding:10px;
}
.sb-card h3{
  margin:0 0 8px 0;
  font-size:11px;
  color:var(--muted);
  font-weight:800;
  letter-spacing:0.3px;
  display:flex;
  align-items:center;
  gap:6px;
}
.sb-icon{
  width:24px; height:24px;
  border-radius:8px;
  background:#eaf4fb;
  display:flex; align-items:center; justify-content:center;
  color:var(--blue);
  font-weight:800;
  font-size:12px;
  flex:0 0 auto;
}

/* Identity fields */
.sb-grid3{
  display:grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap:6px;
}
.sb-copyBtn{
  width:100%;
  text-align:left;
  border:1px solid var(--line);
  border-radius: 10px;
  padding:7px 8px;
  background:#fbfdff;
  cursor:pointer;
  transition: all 0.15s var(--ease);
  outline: 0 solid transparent;
}
.sb-copyBtn:hover{
  border-color: rgba(11,143,212,0.3);
  background:#f6faff;
}
.sb-copyBtn:active{ transform: scale(0.98); }
.sb-copyBtn .sb-k{ font-size:10px; color:var(--muted); font-weight:800; display:flex; align-items:center; gap:4px; flex-wrap:wrap; }
.sb-copyBtn .sb-v{
  margin-top:3px; font-weight:800; font-size:12px;
  display:flex; justify-content:space-between; gap:6px; align-items:center;
}
.sb-src{
  font-size:9px; font-weight:700; padding:1px 5px;
  border-radius:4px; border:1px solid;
  white-space:nowrap;
}
.sb-src.carte-vitale{ background:#e6f7ee; border-color:#a7e3bc; color:#1b6e3a; }
.sb-src.ia{ background:#fef3e2; border-color:#f5d08a; color:#7a4e00; }
.sb-src.patient{ background:#eef4ff; border-color:#c0d9f7; color:#1a4e8a; }
.sb-src.nir{ background:#f3eeff; border-color:#c5aff5; color:#4a1e8a; }
/* 16/09, Nicolas : le violet dit « lu par une machine » — Datamatrix INS, CNI,
   passeport, e-ordo. Ils partagent donc la pastille nir ci-dessus, et il n'y a
   pas de couleur par type de code : ce serait une couleur de plus a lire.
   (Pas d'accent grave dans ce commentaire : il vit dans un gabarit JS.) */
/*
  Le point d'exclamation d'une valeur hors format, et son infobulle.

  Retour du 08/09 : « quand une valeur n'est pas dans le format attendu,
  ajouter un petit point d'exclamation pour attirer l'oeil serait parfait.
  Peut etre avec une petite info bulle : Format inattendu... »

  L'infobulle est l'attribut title natif, pas un panneau maison : il survit au
  defilement, au zoom du navigateur et a la lecture d'ecran, ce qu'aucune
  infobulle en CSS ne fait sans y passer trente lignes. Le prix a payer est
  son delai d'apparition, une seconde environ ; pour un signal qu'on ne lit
  qu'apres avoir vu le point rouge, c'est acceptable.

  La couleur ne porte PAS l'information a elle seule : le caractere « ! » se
  lit meme en noir et blanc et pour un daltonien.
*/
/*
  « Format inattendu » : un message EN LIGNE, sous la valeur.

  Retour du 08/09 (15h55 puis 19h28) : « la bulle d'info en noire s'affiche en
  dessous des noms des patients, est-ce que tu peux la faire afficher dans
  l'autre sens ? », puis « le popup continue d'etre mal place ».

  Trois positionnements ont ete tentes dans la journee — au-dessus, en dessous,
  puis en bascule sur la derniere colonne — et chacun recouvrait quelque chose
  ou sortait de son cadre. Une bulle flottante dans une grille de tuiles n'a
  pas de bonne position : elle est toujours par-dessus la tuile d'a cote.

  On arrete donc de la faire flotter. Le message s'ecrit sous la valeur, dans
  le flux, exactement comme la tablette le fait deja sous ses champs de saisie
  (c'est le motif que Nicolas a sous les yeux tous les jours). Il ne peut plus
  etre mal place, il ne recouvre rien, il se lit sans survol, et il survit a
  l'impression comme a la lecture d'ecran.

  Le point d'exclamation reste : c'est lui qui attire l'oeil dans une grille de
  dix champs, et c'est ce qui avait ete demande le 08/09 au matin.
*/
.sb-alerteFormat{
  font-size:10px; font-weight:900; line-height:1;
  color:#b3261e; background:#fdecea; border:1px solid #f5c2be;
  border-radius:999px; padding:1px 5px;
  white-space:nowrap;
}
.sb-msgFormat{
  margin-top:4px;
  font-size:10px; font-weight:600; line-height:1.35;
  color:#b3261e;
  white-space:normal; text-align:left;
}
/* Sur la tuile, la valeur douteuse porte aussi un lisere : le point seul se
   perd dans une grille de dix champs. */
.sb-copyBtn.sb-horsFormat{ border-color:#f5c2be; background:#fffafa; }
/* Barcode dossier */
.sb-barcodeWrap{ display:flex; flex-direction:column; align-items:flex-start; gap:2px; cursor:pointer; }
.sb-barcodeWrap svg{ max-width:140px; height:36px; display:block; }
.sb-barcodeId{ font-size:15px; color:#fff; font-family:monospace; font-weight:800; word-break:break-all; letter-spacing:0.5px; }
.sb-barcodeId b{ color:#fff; }

.sb-topCopyRow{
  display:flex; gap:4px; flex-wrap:wrap; align-items:center;
  margin-bottom:8px;
}

.sb-btnMini{
  padding:6px 10px;
  border-radius: 10px;
  border:1px solid var(--line);
  background:#fff;
  font-weight:700;
  cursor:pointer;
  font-size:11px;
  white-space:nowrap;
  transition: all 0.15s var(--ease);
}
.sb-btnMini:hover{ box-shadow: 0 2px 6px rgba(0,0,0,0.06); transform: translateY(-1px); }
.sb-btnMini:active{ transform: translateY(0); }
.sb-btnMini.sb-ok{ background:#e9fbf2; border-color:#bff1d6; color:#0b6f3b; }
.sb-btnMini.sb-ok:hover{ background:#dcf8ea; }
.sb-btnMini.sb-blue{ background:#eef6ff; border-color:#cfe6ff; color:#1d5fa1; }
.sb-btnMini.sb-blue:hover{ background:#e2f0ff; }
.sb-btnMini:disabled{ opacity:0.4; cursor:not-allowed; transform:none !important; box-shadow:none !important; }

/* La ligne du resume PDF du dossier : ses deux boutons, a droite, au-dessus
   des documents (Nicolas, 18/09). */
.sb-resumeLigne{ display:flex; gap:6px; flex-wrap:wrap; justify-content:flex-end; padding-bottom:8px; margin-bottom:8px; border-bottom:1px solid var(--line); }

.sb-btnMini2{
  padding:5px 8px;
  border-radius: 8px;
  border:1px solid var(--line);
  background:#fff;
  font-weight:700;
  cursor:pointer;
  font-size:10px;
  white-space:nowrap;
  transition: all 0.15s var(--ease);
}
.sb-btnMini2:hover{ box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.sb-btnMini2.sb-ok{ background:#e9fbf2; border-color:#bff1d6; color:#0b6f3b; }
.sb-btnMini2.sb-remove{ background:#fbfdff; color:#38586f; }
.sb-btnMini2:disabled{ opacity:0.4; cursor:not-allowed; }

.sb-inactive{ opacity:0.5; cursor:pointer !important; }
.sb-inactive:hover{ opacity:0.65; }

.sb-comingSoon{
  font-size:9px;
  color:var(--muted);
  font-weight:700;
  font-style:italic;
  margin-left:4px;
}

/* Edit panel */
.sb-editToggleRow{ display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; align-items:center; }
.sb-editPanel{
  max-height:0;
  overflow:hidden;
  margin-top:6px;
  border:1px dashed transparent;
  border-radius: 10px;
  padding:0 8px;
  background:#fbfdff;
  transition: max-height 0.3s var(--ease), padding 0.3s var(--ease), border-color 0.3s var(--ease);
}
.sb-editPanel.sb-show{
  max-height:200px;
  padding:8px;
  border-color: var(--line);
}
.sb-editGrid{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)) auto;
  gap:6px;
  /* Les champs portent desormais un titre au-dessus d'eux : c'est le BAS des
     cases qui doit s'aligner avec le bouton, pas leur milieu. */
  align-items:end;
}
.sb-editChamp{ display:flex; flex-direction:column; gap:3px; }
.sb-editLabel{ font-size:10px; font-weight:800; color:var(--muted); }
.sb-root input{
  border:1px solid var(--line);
  border-radius: 10px;
  padding:7px 8px;
  font-weight:700;
  background:#fff;
  width:100%;
  font-size:11px;
  transition: border-color 0.2s var(--ease);
}
.sb-root input:focus{
  outline:none;
  border-color: var(--blue);
  box-shadow: 0 0 0 2px rgba(11,143,212,0.1);
}

/* Pills & tags */
.sb-row{ display:flex; gap:6px; flex-wrap:wrap; align-items:center; }
.sb-pill{
  display:inline-flex; align-items:center; gap:5px;
  padding:4px 8px;
  border-radius:999px;
  background:#f3f7fb;
  color:#21435a;
  font-weight:700;
  border:1px solid var(--line);
  white-space:nowrap;
  font-size:11px;
}
.sb-pill.sb-warn{ background:#fff4dd; border-color:#ffe2a1; color:#8a5b00; }
.sb-pill.sb-ok{ background:#e9fbf2; border-color:#bff1d6; color:#0b6f3b; }
.sb-pill.sb-mini{ font-size:9px; padding:2px 5px; }

/* Tubes */
.sb-tubeWrap{ display:flex; gap:6px; flex-wrap:wrap; }
.sb-tubeChip{
  display:inline-flex; align-items:center; gap:5px;
  padding:5px 10px;
  border-radius: 999px;
  border:1px solid var(--line);
  background:#fbfdff;
  font-weight:700;
  font-size:11px;
  white-space:nowrap;
}
.sb-tubeDot{ width:8px; height:8px; border-radius:99px; background:var(--tube-gris); }
.sb-tubeDot.sb-violet{ background:var(--tube-violet); }
.sb-tubeDot.sb-vert{ background:var(--tube-vert); }
.sb-tubeDot.sb-jaune{ background:var(--tube-jaune); }
.sb-tubeDot.sb-bleu{ background:var(--tube-bleu); }
.sb-tubeDot.sb-gris{ background:var(--tube-gris); }

/* Documents viewer */
.sb-split{ display:flex; flex-direction:column; gap:8px; }
.sb-viewer{
  min-height:0;
  display:grid;
  grid-template-columns: minmax(160px, 200px) 1fr;
  gap:8px;
}
.sb-thumbs{ padding-right:2px; }
.sb-docItem{
  border:1px solid var(--line);
  background:#fbfdff;
  border-radius:10px;
  padding:8px;
  margin-bottom:6px;
  cursor:pointer;
  font-size:11px;
  transition: all 0.15s var(--ease);
}
.sb-docItem:hover{ border-color: rgba(11,143,212,0.25); background:#f8fbff; }
.sb-docItem.sb-active{ outline:2px solid rgba(11,143,212,0.4); background:#f4f9ff; }
.sb-docTop{ display:flex; justify-content:space-between; gap:6px; align-items:center; }
.sb-docTitle{ font-weight:800; font-size:11px; }
.sb-docActionsLine{
  margin-top:6px;
  display:flex;
  gap:4px;
  flex-wrap:wrap;
}

.sb-docStatus{
  font-size:9px; font-weight:800;
  padding:3px 6px; border-radius:999px;
  border:1px solid var(--line);
  background:#f3f7fb;
  color:#21435a;
  white-space:nowrap;
}
.sb-docStatus.sb-not{ background:#fff4dd; border-color:#ffe2a1; color:#8a5b00; }
.sb-docStatus.sb-sent{ background:#eef6ff; border-color:#cfe6ff; color:#1d5fa1; }
.sb-docStatus.sb-int{ background:#e9fbf2; border-color:#bff1d6; color:#0b6f3b; }

.sb-preview{
  border:1px solid var(--line);
  border-radius:12px;
  overflow:hidden;
  display:flex; flex-direction:column;
  background:#fff;
  position:relative;
}
.sb-prevTop{
  padding:8px 10px;
  background:#fbfdff;
  border-bottom:1px solid var(--line);
  display:flex; justify-content:space-between; align-items:center; gap:6px;
  flex-wrap:wrap;
  flex-shrink:0;
}
.sb-prevLeft .sb-title{ font-weight:800; font-size:12px; }
.sb-prevLeft .sb-meta{ color:var(--muted); font-size:10px; margin-top:2px; }
.sb-root select{
  border:1px solid var(--line);
  border-radius:10px;
  padding:6px 8px;
  font-weight:700;
  background:#fff;
  font-size:11px;
}
.sb-root select:focus{ outline:none; border-color:var(--blue); }
.sb-root select:disabled{ opacity:0.4; cursor:not-allowed; }
.sb-canvas{
  overflow:hidden;
  display:flex;
  align-items:center;
  justify-content:center;
  color:var(--muted);
  font-weight:700;
  background: radial-gradient(ellipse at center, rgba(11,143,212,0.04), transparent 60%);
  text-align:center;
  font-size:11px;
  position:relative;
  height:62vh;
  min-height:360px;
}
.sb-canvas img{
  max-height:100%;
  max-width:100%;
  width:auto;
  height:auto;
  border-radius:6px;
  user-select:none;
  display:block;
  cursor:grab;
  transform-origin:center center;
  transition:transform 0.1s ease;
}
.sb-canvas img.sb-dragging{ cursor:grabbing; }
.sb-zoomHint{
  position:absolute;
  top:8px;
  right:8px;
  background:rgba(255,255,255,0.85);
  border:1px solid var(--line);
  border-radius:6px;
  padding:3px 7px;
  font-size:14px;
  cursor:zoom-in;
  pointer-events:all;
  box-shadow:0 1px 4px rgba(0,0,0,0.1);
  user-select:none;
}
.sb-canvas:hover .sb-zoomHint{ background:rgba(255,255,255,1); }
.sb-zoomFloat{
  position:absolute;
  bottom:12px;
  left:50%;
  transform:translateX(-50%);
  display:flex;
  gap:8px;
  align-items:center;
  background:rgba(11,27,42,0.72);
  backdrop-filter:blur(6px);
  border-radius:999px;
  padding:6px 14px;
  z-index:20;
}
.sb-zoomFloatBtn{
  background:rgba(255,255,255,0.15);
  border:none;
  color:#fff;
  font-size:18px;
  font-weight:700;
  width:32px; height:32px;
  border-radius:50%;
  cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition:background 0.15s;
}
.sb-zoomFloatBtn:hover{ background:rgba(255,255,255,0.3); }

/* Analyses */
.sb-analysisTop{
  display:flex; gap:6px; align-items:center; flex-wrap:wrap;
  margin-bottom:8px;
}
.sb-copyWide{
  flex:1;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:6px;
  border:1px solid var(--line);
  border-radius: 10px;
  background:#fbfdff;
  padding:8px;
  min-width: 180px;
}
.sb-copyWide .sb-label{ font-size:10px; color:var(--muted); font-weight:800; }
.sb-copyWide .sb-value{
  font-weight:800;
  font-size:11px;
  color:#1b3b52;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.sb-analysisList{ padding-right:2px; }
.sb-sectionTitle{
  margin:8px 0 6px;
  font-size:10px;
  color:var(--muted);
  font-weight:800;
  letter-spacing:0.3px;
  text-transform:uppercase;
}
.sb-aRow{
  border:1px solid var(--line);
  border-radius: 10px;
  background:#fff;
  padding:8px;
  margin-bottom:6px;
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  align-items:center;
  font-size:11px;
}
.sb-aRow:hover{ border-color: rgba(11,143,212,0.2); }
.sb-aRow > div:first-child{ flex:1; min-width:140px; }
.sb-aTitle{ font-weight:800; font-size:11px; }
.sb-aSub{ color:var(--muted); font-size:10px; margin-top:3px; display:flex; gap:4px; flex-wrap:wrap; }
.sb-tag{ display:inline-flex; align-items:center; gap:4px; padding:3px 6px; border-radius:999px; border:1px solid var(--line); background:#f3f7fb; font-weight:700; font-size:9.5px; color:#21435a; }
.sb-tag.sb-ai{ background:#eef6ff; border-color:#cfe6ff; color:#1d5fa1; }
.sb-tag.sb-v{ background:#e9fbf2; border-color:#bff1d6; color:#0b6f3b; }
.sb-tag.sb-nv{ background:#fff4dd; border-color:#ffe2a1; color:#8a5b00; }

.sb-who{ display:flex; flex-direction:column; gap:2px; align-items:flex-end; flex-shrink:0; }
.sb-who .sb-v{ font-weight:800; font-size:10px; }
.sb-who .sb-t{ color:var(--muted); font-size:9px; text-align:right; }

.sb-aActions{ display:flex; gap:4px; justify-content:flex-end; flex-shrink:0; }

/* Les discordances en tableau (Nicolas, 18/09 : « J'imagine un tableau avec
   en en-tete le nom des sources »). Une colonne par source, une ligne par
   champ, et la valeur gardee en vert -- « qu'on ait bien la visu de ce qu'on
   a choisi ». Le tableau defile horizontalement plutot que d'ecraser les
   colonnes : sur un champ a quatre sources, une colonne de 40 px ne se lit
   plus. */
.sb-discordTable{ overflow-x:auto; margin:8px 0 10px; }
.sb-discordTable table{ border-collapse:separate; border-spacing:0 4px; width:100%; font-size:12px; }
.sb-discordTable th{ text-align:left; font-size:9.5px; font-weight:800; text-transform:uppercase; letter-spacing:.3px; color:var(--muted); padding:0 6px 2px; white-space:nowrap; }
.sb-discordChamp{ position:sticky; left:0; background:#fff; z-index:1; }
.sb-discordTable tbody th{ font-size:11px; text-transform:none; letter-spacing:0; color:var(--text); font-weight:700; padding-right:10px; vertical-align:middle; }
.sb-discordTable td{ padding:0 3px; vertical-align:middle; }
.sb-discordVide{ color:var(--muted); text-align:center; font-weight:700; }
.sb-discordCase{ display:flex; align-items:center; gap:6px; width:100%; text-align:left; cursor:pointer; font-family:inherit; font-size:12px; font-weight:700; color:var(--text); background:#f8fafc; border:1px solid var(--line); border-radius:9px; padding:6px 9px; transition:border-color .12s, background .12s; }
.sb-discordCase:hover{ border-color:#94a3b8; background:#fff; }
.sb-discordRetenue{ background:#e9fbf2; border-color:#10B981; box-shadow:inset 0 0 0 1px #10B981; color:#0b6f3b; }
.sb-discordCoche{ margin-left:auto; color:#0b6f3b; font-weight:900; }
.sb-discordValeur{ display:flex; flex-direction:column; gap:1px; min-width:0; }
.sb-discordAussi{ font-size:9px; font-weight:600; color:var(--muted); text-transform:lowercase; letter-spacing:0; }

/* La carte de mutuelle (Nicolas, 18/09 : « c'est aussi tres moche »). Les
   deux numeros qu'on recopie dans le SIL passent devant, en clic/copie ; la
   validite se lit au lisere et a la pastille ; le reste se deplie comme sur
   une carte.

   valeurs 12px comme .sb-v, libelles 10px comme .sb-k, poids 600 au plus.
   Rien dans la carte ne depasse 12px : c'est une carte de LECTURE, pas un
   pave de saisie, et la secretaire la lit entre deux champs du SIL.

   01/10 AU SOIR, le client : « la taille est toujours trop grande, regarde
   la phrase sur la biologie prise a 100 % ». Une carte qu'on CONSULTE ne se
   lit pas comme un pave : tout passe a 11px (libelles 10px), poids 500 au
   plus -- 600 seulement pour les numeros, qu'on compare caractere a
   caractere. La ligne de biologie, elle, devient une ligne discrete.

   La carte redevient NEUTRE, aussi. Le violet est la marque de la lecture
   automatique, et le client le veut la ou il recopie : sur les chips de
   l'identite (.sb-mutChips). Ici, seules les ALERTES gardent leur ambre
   (sb-nv) et l'etiquette « Lu automatiquement » son violet -- elle dit d'ou
   vient tout le reste et ne se deduit pas de la position. */
.sb-mutCard h3{ display:flex; align-items:center; gap:8px; }
/* Les lignes du detail passent par .sb-copyBtn, fait pour les TUILES
   d'identite : valeurs 12px en 800. La carte n'est pas un pave de saisie, on
   les ramene donc a l'echelle de la carte -- libelles 10px, valeurs 11px en
   500. Seuls les numeros gardent 600 (voir .sb-mutNumValeur). */
.sb-mutCard .sb-copyBtn{ padding:5px 8px; }
.sb-mutCard .sb-copyBtn .sb-k{ font-size:10px; font-weight:500; }
.sb-mutCard .sb-copyBtn .sb-v{ font-size:11px; font-weight:500; }
.sb-mutNom{ font-size:11px; font-weight:500; color:var(--muted); text-transform:none; letter-spacing:0; }
.sb-mutValidite{ display:flex; align-items:center; gap:8px; border:1px solid; border-radius:10px; padding:6px 9px; font-size:11px; font-weight:500; margin-bottom:8px; }
.sb-mutPastille{ display:inline-flex; align-items:center; justify-content:center; width:16px; height:16px; border-radius:999px; color:#fff; font-size:10px; font-weight:600; flex-shrink:0; }
.sb-mutNums{ display:flex; gap:8px; flex-wrap:wrap; }
/* Neutre, comme le reste du dossier : la carte n'a plus a annoncer sa
   provenance par la couleur, c'est l'etiquette du titre qui le fait. */
.sb-mutNum{ flex:1 1 150px; text-align:left; cursor:pointer; font-family:inherit; background:#fbfdff; border:1px solid var(--line); border-radius:11px; padding:6px 9px; transition:border-color .12s, background .12s; }
.sb-mutNum:hover{ border-color:var(--blue); background:#fff; }
/* Nicolas, 18/09 18h18 : « les numero d'AMC et d'Adherent sont trop gros ».
   01/10, puis le soir : ils gardent la chasse fixe -- c'est un numero qu'on
   compare caractere a caractere -- et le poids 600, la seule exception a la
   regle du 500, mais ils rentrent dans la ligne a 11px. */
.sb-mutNumValeur{ display:block; margin-top:2px; font-family:monospace; font-size:11px; font-weight:600; color:#0b1b2a; letter-spacing:.4px; }
/* La validite quand la carte est repliee : un rond de couleur, pas une ligne. */
.sb-mutRond{ display:inline-flex; align-items:center; justify-content:center; width:14px; height:14px; border-radius:999px; color:#fff; font-size:10px; font-weight:600; flex-shrink:0; margin-left:2px; }
.sb-mutTaux{ margin-top:6px; }
.sb-mutTauxValeur{ font-size:11px; font-weight:500; color:#0b6f3b; }
.sb-mutTauxPartiel{ color:#8a5b00; }
.sb-mutTauxAbsent{ font-size:11px; font-weight:500; color:var(--muted); font-style:italic; }
.sb-mutAssures{ display:flex; flex-direction:column; gap:4px; margin-top:4px; }
.sb-mutAssure{ display:flex; align-items:baseline; gap:8px; text-align:left; cursor:pointer; font-family:inherit; background:#f8fafc; border:1px solid var(--line); border-radius:9px; padding:5px 9px; }
.sb-mutAssure:hover{ border-color:var(--blue); background:#fff; }
.sb-mutAssureNom{ font-size:11px; font-weight:500; color:var(--text); }
.sb-mutAssureDdn{ margin-left:auto; font-size:10px; color:var(--muted); font-family:monospace; }

/*
   LES PASTILLES DU DOSSIER, ET L'ETIQUETTE DE LECTURE.

   Dans la CARTE, tout ce qui est montre est neutre sauf l'alertes : un
   avertissement n'est pas une provenance, et deux informations differentes ne
   portent pas la meme couleur. Dans les CHIPS DE L'IDENTITE, c'est l'inverse
   -- tout est violet, parce que tout y vient de la meme lecture.
*/
.sb-mutCard .sb-pill{ background:#f3f7fb; border-color:var(--line); color:#21435a; }
.sb-mutCard .sb-pill.sb-nv{ background:#fff4dd; border-color:#ffe2a1; color:#8a5b00; }
/* L'etat de la lecture, quand il n'y a pas encore de resume a montrer. */
.sb-mutLecture{
  display:flex; align-items:center; gap:8px; flex-wrap:wrap;
  font-size:11px; font-weight:500; color:var(--muted);
}

/*
   LES CHIPS DE LA MUTUELLE, DANS LE BLOC IDENTITE.

   01/10 au soir, le client : « le violet n'est pas au bon endroit ». Il le
   veut EN HAUT, dans « Identite (clic = copie) », sur les quatre ou cinq
   valeurs qu'il recopie vraiment dans le SIL : nom de la mutuelle, numero
   AMC, numero d'adherent, taux de biologie, validite.

   Meme violet que .sb-src.nir -- c'est la regle du produit depuis le 16/09,
   une seule couleur pour « lu par une machine » -- et copiables au clic comme
   le reste du bloc, donc des boutons et pas des pastilles decoratives.
*/
.sb-mutChips{
  display:flex; flex-wrap:wrap; gap:4px; align-items:center;
  margin:4px 0 8px;
}
.sb-mutChip{
  font-family:inherit; font-size:10px; font-weight:600; line-height:1.2;
  background:#f3eeff; border:1px solid #c5aff5; color:#4a1e8a;
  border-radius:999px; padding:3px 8px; cursor:pointer;
  white-space:nowrap; transition:background .12s;
}
.sb-mutChip:hover{ background:#fff; }
.sb-mutChipValide{ background:#e9fbf2; border-color:#a7e3bc; color:#0b6f3b; }
.sb-mutChipExpiree{ background:#fff1f2; border-color:#f5c2be; color:#9f1239; }

/* Footer */
.sb-footer{
  position:sticky;
  bottom:0;
  padding:12px 8px 8px;
  display:flex;
  justify-content:center;
  gap:8px;
  background: linear-gradient(to top, var(--bg) 70%, transparent);
  z-index:10;
}
.sb-btnDossierCree{
  padding:10px 28px;
  border-radius: 12px;
  border:none;
  background: linear-gradient(135deg, var(--ok), #17a85c);
  color:#fff;
  font-weight:800;
  font-size:13px;
  cursor:pointer;
  box-shadow: 0 3px 12px rgba(31,191,106,0.25);
  transition: all 0.2s var(--ease);
}
.sb-btnDossierCree:hover{ transform: translateY(-1px); box-shadow: 0 6px 20px rgba(31,191,106,0.3); }
.sb-btnDossierCree:active{ transform: translateY(0); }
.sb-btnDossierCree:disabled{ opacity:0.5; cursor:not-allowed; transform:none !important; }
.sb-footerNote{ padding: 0 16px 14px; }

/* Statuts du dossier (retour du 04/09). Le violet d'« À vérifier » attire
   l'oeil sans dire « faute » : le dossier attend un second regard. */
.sb-pillAVerifier{
  background:#f3ecff; color:#5b21b6; border:1px solid #d9c7ff;
}
.sb-apptItem.sb-aVerifier{ border-left:3px solid #7C3AED; }
.sb-btnAVerifier{
  padding:10px 22px; border-radius:12px; border:1px solid #d9c7ff;
  background:#f3ecff; color:#5b21b6; font-weight:800; font-size:13px;
  cursor:pointer; transition: all 0.2s var(--ease);
}
.sb-btnAVerifier:hover{ transform: translateY(-1px); }
.sb-btnAVerifier.sb-actif{ background:#7C3AED; color:#fff; border-color:#7C3AED; }
.sb-commentaire{
  margin-top:8px; width:100%; box-sizing:border-box; resize:vertical;
  min-height:58px; border:1px solid var(--line); border-radius:10px;
  padding:8px 10px; font-size:12px; font-family:inherit; background:#fbfdff;
}
.sb-commentaireLigne{ display:flex; gap:6px; align-items:flex-start; }
.sb-commentaireLigne .sb-commentaire{ flex:1; }
.sb-btnOkCommentaire{
  align-self:stretch; padding:0 14px; border-radius:10px; border:none;
  background: var(--blue); color:#fff; font-weight:800; font-size:12px;
  cursor:pointer; flex-shrink:0;
}
.sb-btnOkCommentaire:disabled{ opacity:0.4; cursor:default; }

/* Commentaire du preleveur : copiable au clic, comme les champs du bloc
   Identite (demande de Nicolas du 05/10/2026). Sauts de ligne conserves. */
.sb-copieTexte{
  font-size:12px; font-weight:600; color:var(--text); line-height:1.5;
  white-space:pre-wrap; cursor:pointer;
}

/* Historique des statuts, dans une bulle (retour du 04/09) : on veut savoir
   qui a pose quoi et quand, sans que cela occupe l'ecran en permanence. */
.sb-histoAncre{ position:relative; display:inline-block; }
.sb-histoBulle{
  position:absolute; bottom:calc(100% + 8px); right:0; z-index:40;
  width:280px; max-height:230px; overflow-y:auto;
  background:#fff; border:1px solid var(--line); border-radius:12px;
  box-shadow:0 10px 30px rgba(0,0,0,0.16); padding:10px 12px; text-align:left;
}
.sb-histoTitre{ font-size:11px; font-weight:800; color:var(--muted); margin-bottom:6px; }
.sb-histoLigne{ font-size:11px; color:#0b1b2a; padding:4px 0; border-top:1px solid var(--line); }
.sb-histoLigne:first-of-type{ border-top:none; }
.sb-histoQuand{ color:var(--muted); font-weight:600; }
.sb-commentaireBas{ display:flex; gap:10px; align-items:center; justify-content:space-between; flex-wrap:wrap; }
.sb-commentaireMeta{ font-size:10px; color:var(--muted); margin-top:4px; font-style:italic; }

/* Toast */
.sb-toast{
  position:fixed; bottom:12px; right:12px;
  background:#0b1b2a;
  color:#fff;
  padding:8px 12px;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.2);
  font-weight:700;
  font-size:11px;
  max-width: 360px;
  z-index: 9999;
  line-height:1.3;
  opacity:0;
  transform: translateY(8px);
  transition: opacity 0.25s var(--ease), transform 0.25s var(--ease);
  pointer-events:none;
}
.sb-toast.sb-show{
  opacity:1;
  transform: translateY(0);
  pointer-events:auto;
}

/* Confirmation avant de quitter un dossier travaille mais non marque cree. */
.sb-confirmOverlay{
  position:fixed; inset:0;
  background:rgba(11,27,42,0.45);
  display:flex; align-items:center; justify-content:center;
  z-index:10000;
}
.sb-confirmBox{
  background:#fff;
  border-radius:16px;
  padding:20px 22px;
  width:min(460px, 92vw);
  box-shadow:0 20px 60px rgba(0,0,0,0.25);
  border-top:4px solid var(--warn);
}
.sb-confirmTitle{ font-size:15px; font-weight:900; margin-bottom:8px; }
.sb-confirmText{ font-size:13px; color:var(--muted); line-height:1.5; font-weight:600; }
.sb-confirmActions{
  display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; margin-top:16px;
}

/* Bandeau de verification : rien ne s affiche quand tout va bien. */
.sb-verifBanner{
  display:flex; align-items:center; gap:10px;
  background:#FEF2F2;
  border:1px solid #FECACA;
  border-left:5px solid #C0392B;
  border-radius:12px;
  padding:10px 12px;
  margin-bottom:10px;
  cursor:pointer;
}
.sb-verifBannerTitle{ font-size:13px; font-weight:800; color:#991B1B; }
.sb-verifBannerText{ font-size:12px; color:#7F1D1D; font-weight:600; line-height:1.45; }
.sb-verifPanel{
  border:1px solid var(--line);
  border-radius:12px;
  padding:12px;
  margin-bottom:10px;
  background:#fbfdff;
}
.sb-verifRow{
  display:flex; gap:10px; align-items:flex-start;
  padding:8px 0; border-bottom:1px solid var(--line);
}
.sb-verifRow:last-child{ border-bottom:none; }
.sb-verifChamp{ width:150px; font-size:12px; font-weight:800; color:var(--text); padding-top:7px; }
.sb-verifInput{
  width:100%; border:1px solid var(--line); border-radius:8px;
  padding:6px 8px; font-size:13px; font-weight:700; font-family:inherit;
}
.sb-verifLu{ font-size:11px; color:var(--muted); font-style:italic; margin-top:3px; }
.sb-verifEtat{
  font-size:10px; font-weight:900; padding:3px 8px; border-radius:999px;
  white-space:nowrap;
}
.sb-verifEtat.conforme{ background:#ECFDF5; color:#065F46; }
.sb-verifEtat.nonconforme{ background:#FEF2F2; color:#991B1B; }
.sb-verifJournal{ margin-top:10px; border-top:1px solid var(--line); padding-top:8px; }
.sb-verifEvent{ font-size:11px; color:#4B5563; padding:4px 0; border-bottom:1px solid #F3F4F6; }

/* Loading */
.sb-loading{
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:30px; gap:8px; color:var(--muted);
}
.sb-spinner{
  width:28px; height:28px;
  border:3px solid var(--line);
  border-top-color:var(--blue);
  border-radius:50%;
  animation: sb-spin 0.8s linear infinite;
}
@keyframes sb-spin { to { transform:rotate(360deg); } }

/* Zoom overlay */
.sb-zoomOverlay{
  position:fixed;
  top:0; left:0; right:0; bottom:0;
  background:rgba(0,0,0,0.92);
  z-index:99999;
  display:flex;
  align-items:center;
  justify-content:center;
  overflow:hidden;
}
.sb-zoomImgWrap{
  position:relative;
  cursor:grab;
}
.sb-zoomImgWrap.sb-grabbing{ cursor:grabbing; }
.sb-zoomImgWrap img{
  border-radius:4px;
  user-select:none;
  -webkit-user-drag:none;
  pointer-events:none;
}
.sb-zoomToolbar{
  position:fixed;
  top:12px; left:50%; transform:translateX(-50%);
  display:flex; gap:6px; align-items:center;
  z-index:100000;
  background:rgba(0,0,0,0.7);
  padding:6px 12px;
  border-radius:999px;
  backdrop-filter:blur(8px);
}
.sb-zoomBtn{
  background:rgba(255,255,255,0.15);
  border:none;
  color:#fff;
  font-size:18px;
  width:34px; height:34px;
  border-radius:50%;
  cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  font-weight:700;
  transition: background 0.15s;
}
.sb-zoomBtn:hover{ background:rgba(255,255,255,0.3); }
.sb-zoomLevel{ color:#fff; font-size:12px; font-weight:700; min-width:44px; text-align:center; }

/* Responsive */
@media (max-width: 1100px){
  .sb-content{ grid-template-columns: 1fr; }
}
@media (max-width: 900px){
  .sb-sidebar{ width:240px; min-width:240px; }
  .sb-viewer{ grid-template-columns: 1fr; }
}
@media (max-width: 700px){
  .sb-layout{ flex-direction:column; }
  .sb-sidebar{ width:100%; min-width:100%; max-height:35vh; }
  .sb-content{ padding:6px; }
}

/* ── Fenêtre « Code lu » ──────────────────────────────────────────────────
   Arrivée de la tablette le 02/09. Le code brut est en police à chasse fixe
   et il DÉBORDE horizontalement plutôt que de se replier : une coupure de
   ligne au milieu d'un matricule se lit comme un caractère manquant, et c'est
   précisément ce qu'on vient vérifier ici. */
.cl-fond {
  position: fixed; inset: 0; z-index: 60;
  background: rgba(11, 27, 42, .55);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.cl-fenetre {
  background: var(--card); border-radius: 14px; width: min(720px, 100%);
  max-height: 88vh; display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(11,27,42,.35);
}
.cl-entete {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #e6eef6;
}
.cl-entete h3 { margin: 0; font-size: 14px; color: var(--text); }
.cl-fermer {
  border: 0; background: transparent; font-size: 24px; line-height: 1;
  cursor: pointer; color: var(--muted); padding: 0 4px;
}
.cl-corps { overflow-y: auto; padding: 16px 18px 20px; }
.cl-alerte {
  background: #fff8e6; border: 1px solid #ffe0a3; border-radius: 10px;
  padding: 10px 12px; margin-bottom: 16px; font-size: 12px; color: #8a5b00;
}
.cl-alerte ul { margin: 6px 0 0; padding-left: 18px; }
.cl-section { margin-bottom: 20px; }
.cl-sectionTitre {
  font-size: 12px; font-weight: 800; color: var(--text); margin-bottom: 4px;
}
.cl-explication {
  font-size: 11px; color: var(--muted); margin: 0 0 10px; line-height: 1.5;
}
.cl-brut {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; background: #f7fafd; border: 1px solid #e6eef6;
  border-radius: 8px; padding: 10px; overflow-x: auto; white-space: pre;
  color: var(--text);
}
.cl-longueur { font-size: 11px; color: var(--muted); margin-top: 5px; }
.cl-ligne {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 7px 0; border-bottom: 1px solid #f0f5fa;
}
.cl-ligneTexte { flex: 1; min-width: 0; }
.cl-libelle { font-size: 11px; font-weight: 700; color: var(--muted); }
.cl-note { font-weight: 400; font-style: italic; }
.cl-valeur {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; color: var(--text); word-break: break-all; margin-top: 2px;
}
.cl-absent { color: var(--muted); font-style: italic; font-family: inherit; }
.cl-copier {
  border: 1px solid #cfe3f3; background: #fff; color: var(--blue2);
  border-radius: 7px; padding: 3px 9px; font-size: 11px; font-weight: 700;
  cursor: pointer; flex: none;
}
.cl-copierLarge { margin-top: 8px; }
.cl-pied { font-size: 11px; color: var(--muted); margin-top: 4px; }



`;

// ─── Component ───
// La file de lecture des mutuelles dans le navigateur, gardée pour un retour
// arrière immédiat si la tâche serveur s'arrête (02/10).
const LECTURE_FILE_NAVIGATEUR = false;

export default function SecretaryBackoffice() {
  const { firebase } = useFirebase();

  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Le tri de la colonne gauche : « heure » (defaut), « salle », ou le filtre
  // « direct ». Nicolas, 13/09 : « Mode direct » n'affiche QUE les prises en
  // charge directes — ce n'est pas un tri, c'est un filtre.
  const [triListe, setTriListe] = useState("heure");
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [roomNames, setRoomNames] = useState({});

  const [data, setData] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Fenetre « Code lu », arrivee de la tablette le 02/09.
  const [codeLuOuvert, setCodeLuOuvert] = useState(false);
  // La conversion d'une photo en PDF prend le temps d'un dessin sur canvas.
  // Ce verrou empeche la secretaire de lancer trois conversions en cliquant
  // trois fois parce que « rien ne se passe ».
  const [pdfEnCours, setPdfEnCours] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastVisible, setToastVisible] = useState(false);

  const [docPreviewUrl, setDocPreviewUrl] = useState(null);
  const [docPreviewLoading, setDocPreviewLoading] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const [imgWidthPct, setImgWidthPct] = useState(100); // width % de l'image
  const [inlineZoom, setInlineZoom] = useState(1);
  const [inlinePos, setInlinePos] = useState({ x: 0, y: 0 });
  const [editBarcode, setEditBarcode] = useState(false);
  // Retour du 28/08 : rien n empechait de quitter un dossier sur lequel on
  // avait deja travaille sans avoir clique « Dossier cree ». On note donc les
  // gestes qui montrent qu un dossier a ete travaille (copie d un numero,
  // consultation d un autre document, telechargement, edition), et on demande
  // confirmation avant de passer a autre chose.
  const [dossierTravaille, setDossierTravaille] = useState(false);
  const [dossierEnAttente, setDossierEnAttente] = useState(null);
  // Retours du 04/09. `sortiePage` porte la question posee par le routeur
  // quand on quitte l'ecran ; `creationADemander` celle qu'on pose quand
  // « Dossier cree » est clique sans qu'on ait touche au dossier.
  const [sortiePage, setSortiePage] = useState(null);
  const [creationADemander, setCreationADemander] = useState(false);
  // Le fil de discussion entre secretaires sur un dossier.
  const [commentaire, setCommentaire] = useState("");
  const [commentaireEnregistre, setCommentaireEnregistre] = useState("");
  // Qui est devant l'ecran, sous son nom et non sous son adresse.
  const [moi, setMoi] = useState({ email: "", nom: "" });
  const [histoOuvert, setHistoOuvert] = useState(false);
  // La carte de mutuelle est repliee au depart ; elle s'ouvre quand on clique
  // le document « Mutuelle » a droite (Nicolas, 18/09) et se referme quand on
  // regarde autre chose. Repliee est le bon defaut depuis le 01/10 au soir :
  // l'essentiel (les numeros, la validite) est en haut, dans les chips de
  // l'identite, et la carte n'est plus qu'un detail qu'on va chercher.
  const [mutuelleOuverte, setMutuelleOuverte] = useState(false);
  const mutuelleRef = useRef(null);
  const dragRef = useRef(null);
  const inlineDragRef = useRef(null);
  const barcodeRef = useRef(null);
  const prevSelectedDocIdRef = useRef(null);

  /*
    L'APERCU SE RATTRAPE QUAND LA PHOTO ARRIVE EN RETARD.

    La tablette cree le dossier, puis ecrit les chemins des photos ~8 s plus
    tard (voir « Photos de l'accueil perdues a Laon »). L'apercu, lui, ne se
    demandait qu'une fois : si le chemin n'existait pas encore, la vignette
    restait vide jusqu'au rechargement de la page — et la secretaire recharge.

    `apercuCheminRef` retient le CHEMIN du fichier affiche : c'est lui qui
    change quand la photo arrive, pas le numero du document (le dossier passe
    du meme `doc-mutual` a un `storagePath` renseigne).
  */
  const apercuCheminRef = useRef(null);
  // Les minuteries de reprise de l'apercu, a nettoyer au demontage et a chaque
  // changement de dossier : sans cela une reprise en vol rallume une image du
  // dossier precedent. On garde celles du _dernier_ passage de l'effet pour
  // les couper avant d'en demarrer un autre.
  const apercuReprisesRef = useRef([]);
  // La reprise de l'<img> : une seule fois par fichier affiche (voir
  // `relancerApercuUneFois`). Remis a zero quand le fichier change.
  const apercuRelanceRef = useRef(false);

  // L'etat de la lecture automatique de la carte, par dossier :
  // "lecture" | "illisible". Le resume, lui, arrive par Firestore.
  const [mutuelleLecture, setMutuelleLecture] = useState(null);
  // Un dossier n'est lu QU'UNE FOIS par session (Set) : la liste se rafraichit
  // toutes les 30 s et l'effet se relance a chaque instantane.
  const mutuellesLanceesRef = useRef(new Set());
  // Les dossiers dont la lecture a echoue : leur carte doit le redire a chaque
  // ouverture, sans qu'on relance pour autant (voir l'effet plus bas).
  const mutuellesEchecsRef = useRef(new Set());
  const mutuelleRelanceTimerRef = useRef(null);
  const dossierOuvertRef = useRef(null);

  // Le nom de la personne connectee, pour signer ses messages et les statuts
  // qu'elle pose. On lit le document `users/{email}`, celui que le reste du
  // back-office utilise deja. Sans lui, on reconstitue le nom depuis
  // l'adresse plutot que de l'afficher telle quelle.
  useEffect(() => {
    const email = firebase.auth?.currentUser?.email || "";
    if (!email) return;
    setMoi({ email, nom: nomLisible(email) });
    getDoc(doc(firebase.db, "users", email))
      .then((snap) => {
        if (!snap.exists()) return;
        const u = snap.data();
        const complet = `${u.firstName || u.name || ""} ${u.lastName || ""}`.trim();
        if (complet) setMoi({ email, nom: complet });
      })
      .catch(() => { /* on garde le nom reconstitue depuis l'adresse */ });
  }, [firebase]);

  // Quitter l'ecran par le menu : c'est NOUS qui posons la question, avec la
  // fenetre du produit et les memes trois reponses que pour un changement de
  // dossier. Le routeur ne fait que nous la transmettre.
  useEffect(() => installerPoseurQuestion((message, repondre) => {
    setSortiePage({ message, repondre });
  }), []);

  // Fermeture ou rechargement de l onglet : le navigateur pose lui-meme la
  // question, on se contente de l armer quand un dossier est en travail.
  useEffect(() => {
    const avertir = (e) => {
      if (!dossierNonConclu()) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", avertir);
    return () => window.removeEventListener("beforeunload", avertir);
  });

  /*
    LE DOSSIER OUVERT SUIT LA LISTE.

    ─────────────────────────────────────────────────────────────────────────
    POURQUOI, ET CE QUE CA CHANGE A L'OUVERTURE
    ─────────────────────────────────────────────────────────────────────────

    `data` etait construit UNE fois, a l'ouverture du dossier, a partir du
    rendez-vous tel qu'il etait a cette seconde-la. Deux consequences, et la
    seconde est celle dont le client s'est plaint :

      — le resume de la mutuelle lu par la file n'arrivait JAMAIS dans `data` :
        le dossier ouvert ne voyait donc jamais sa carte apparaitre, et il
        fallait le rouvrir (ou recharger la page) pour la lire ;
      — pour voir une correction arrivee entre-temps — un nouveau document, un
        numero retape a la tablette — il fallait rouvrir le dossier.

    On reconstruit donc `data` a chaque changement de la liste. Le prix est un
    recalcul par instantane ; il est petit, et il est surtout INDEPENDANT de
    l'affichage : la liste et le dossier sont deja a l'ecran, cet effet ne fait
    que rafraichir ce qu'ils montrent.

    On ne reconstruit que si le rendez-vous a REELLEMENT change de contenu (le
    meme objet revient a chaque instantane), et jamais pendant que l'identite
    est en cours d'edition : une valeur tapee mais pas encore validee serait
    ecrasee par la version de Firestore.
  */
  const rendezVousAfficheRef = useRef(null);
  useEffect(() => {
    if (!selectedAppointmentId) return;
    const rdv = appointments.find((a) => a.id === selectedAppointmentId);
    if (!rdv) return;
    if (rendezVousAfficheRef.current === rdv) return;
    // Pendant l'edition de l'identite, on ne reconstruit rien : la valeur
    // tapee mais pas encore validee serait ecrasee par celle de Firestore.
    // Et on ne note PAS le rendez-vous comme affiche : il le sera a la
    // fermeture du panneau, sans qu'on ait rien perdu.
    if (editOpen) return;
    rendezVousAfficheRef.current = rdv;
    setData(transformAppointmentToViewData(rdv, roomNames[rdv.roomId] || ""));
  }, [appointments, selectedAppointmentId, roomNames, editOpen]);

  // Render barcode when dossier changes
  useEffect(() => {
    if (barcodeRef.current && (data?.dossierBarcode || data?.id)) {
      try {
        JsBarcode(barcodeRef.current, data.dossierBarcode || data.id, {
          format: "CODE128",
          lineColor: "#1b3b52",
          width: 1.2,
          height: 36,
          displayValue: false,
          margin: 0,
        });
      } catch (e) { /* ID invalid for barcode - ignore */ }
    }
  }, [data?.dossierBarcode, editBarcode]);


  const [inNir, setInNir] = useState("");
  const [inBirthCity, setInBirthCity] = useState("");
  const [inDossierBarcode, setInDossierBarcode] = useState("");
  // RPPS → SIL code mapping
  const [prescripteurSilCode, setPrescripteurSilCode] = useState(null);
  const [editPrescripteurSil, setEditPrescripteurSil] = useState(false);
  const [inPrescripteurSilCode, setInPrescripteurSilCode] = useState("");

  const toastTimer = useRef(null);

  // Les noms de salle deja lus, pour ne pas relire le meme document a chaque
  // instantane de la liste (voir `processSnapshot`).
  const sallesConnuesRef = useRef({});

  // ─── Fetch appointments ───
  useEffect(() => {
    const currentLab = localStorage.getItem("currentLab");
    if (!currentLab) { setIsLoading(false); return; }

    const daysAgo = subDays(new Date(), 8);
    const q = query(
      collection(firebase.db, "appointements"),
      where("laboratoryId", "==", currentLab),
      where("ended_at", ">=", Timestamp.fromDate(daysAgo)),
      orderBy("ended_at", "desc")
    );

    // Helper: process snapshot results and resolve rooms
    //
    // Nicolas, 16/09 : « pour avoir les dossiers Direct qui s'affichent il faut
    // rafraichir la page, hors les prises en charge classiques s'affichent
    // directement ».
    //
    // La lecture des salles est asynchrone, et la liste n'etait posee qu'APRES
    // elle : deux instantanes qui arrivent coup sur coup se terminaient dans le
    // desordre, et le plus ancien ecrasait le plus recent. Un dossier classique
    // ne le voit pas -- sa creation et sa fin sont separees de plusieurs
    // minutes. Un dossier Mode direct arrive en rafale (creation, puis photos,
    // puis code-barres, en quelques secondes), et c'est exactement la que la
    // course se produit : le dossier disparaissait de la liste jusqu'au
    // rechargement suivant.
    //
    // On numerote donc les passages, et un passage depasse ne pose plus rien.
    let derniereDemande = 0;
    const processSnapshot = async (qSnapshot) => {
      const monTour = ++derniereDemande;
      const temp = [];
      const roomIds = new Set();
      qSnapshot.forEach((d) => {
        const appt = { ...d.data(), id: d.id };
        temp.push(appt);
        if (appt.roomId) roomIds.add(appt.roomId);
      });
      // La liste est posee tout de suite : les noms de salle ne sont qu'un
      // ornement, ils n'ont pas a retenir l'affichage d'un dossier.
      setAppointments(temp);
      setIsLoading(false);
      /*
        LE NOM D'UNE SALLE NE CHANGE PAS TOUTES LES 30 SECONDES.

        Le relevé ci-dessus repassait a chaque instantane : chaque changement
        de la file relisait le document de CHAQUE salle presente dans la liste.
        Avec huit jours de dossiers, cela faisait une poignee de lectures
        Firestore inutiles a chaque fois, pour un nom qui bouge une fois par an.

        On ne demande donc que les salles qu'on ne connait pas encore. Le
        cache vit dans un `ref` : il n'a pas a provoquer de rendu, il ne sert
        qu'a eviter une question dont on a deja la reponse.
      */
      const inconnues = [...roomIds].filter((rid) => !(rid in sallesConnuesRef.current));
      if (!inconnues.length) return;
      const roomMap = {};
      await Promise.all(inconnues.map(async (rid) => {
        try {
          const rd = await getDoc(doc(firebase.db, "rooms", rid));
          if (rd.exists()) roomMap[rid] = rd.data().name;
        } catch (e) { /* ignore */ }
      }));
      if (monTour !== derniereDemande) return;
      sallesConnuesRef.current = { ...sallesConnuesRef.current, ...roomMap };
      setRoomNames(sallesConnuesRef.current);
    };

    // Try realtime listener first
    let unsubscribe = null;
    let fallbackInterval = null;

    try {
      unsubscribe = onSnapshot(q, (snap) => {
        processSnapshot(snap);
      }, (err) => {
        // Listener failed — fallback to polling every 30s
        console.warn("onSnapshot failed, falling back to polling:", err);
        const poll = async () => {
          try {
            const snap = await getDocs(q);
            await processSnapshot(snap);
          } catch (e) { console.error("Polling error:", e); }
        };
        poll();
        fallbackInterval = setInterval(poll, 30000);
      });
    } catch (e) {
      // onSnapshot threw synchronously — fallback
      console.warn("onSnapshot error, falling back to polling:", e);
      const poll = async () => {
        try {
          const snap = await getDocs(q);
          await processSnapshot(snap);
        } catch (err) { console.error("Polling error:", err); }
      };
      poll();
      fallbackInterval = setInterval(poll, 30000);
    }

    return () => {
      if (unsubscribe) unsubscribe();
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, []);

  // ─── Load document preview ───
  useEffect(() => {
    if (!selectedDocId || !data) return;
    // Reset zoom only when the selected doc changes (not on every data update)
    if (selectedDocId !== prevSelectedDocIdRef.current) {
      setImgWidthPct(100);
      setInlineZoom(1);
      setInlinePos({ x: 0, y: 0 });
      prevSelectedDocIdRef.current = selectedDocId;
    }
    const sd = data.documents.find((d) => d.id === selectedDocId);
    if (!sd?.storagePath) { apercuCheminRef.current = null; setDocPreviewUrl(null); return; }
    /*
      `data` change à chaque instantané de la file (écoute en temps réel +
      relevé de 30 s). Avant, chaque changement retirait l'image, remettait
      « Chargement... », redemandait l'URL et retéléchargeait la photo
      (~770 Ko) : page blanche imprévisible à Laon (Nicolas, 30/09). On ne
      recharge plus que si le FICHIER affiché change, et une réponse arrivée
      après un changement de document est ignorée.
    */
    if (apercuCheminRef.current === sd.storagePath) return;
    apercuCheminRef.current = sd.storagePath;
    apercuRelanceRef.current = false;
    const chemin = sd.storagePath;
    /*
      LA PHOTO QUI ARRIVE DANS LE DOSSIER DEJA OUVERT.

      01/10 : la carte de mutuelle du dossier ouvert montre la photo de la
      tablette. Or la tablette ecrit les chemins ~8 s APRES avoir cree le
      dossier. L'effet se relance bien quand `data` change — c'est ce que fait
      la ligne ci-dessus — mais la vignette, elle, ne se rattrapait pas :
      l'objet n'existe pas encore dans le seau, `getDownloadURL` echoue
      (`storage/object-not-found`), et on abandonnait jusqu'au rechargement.

      On reprend donc toutes les 2 s pendant 60 s. Une photo qui met plus
      d'une minute a monter n'existe pas : au-dela on abandonne proprement,
      sans clignoter indefiniment.
    */
    let reference;
    try {
      // ref() lève (et n'est rattrapé par aucun .catch) sur un chemin malformé
      reference = ref(getStorage(), chemin);
    } catch (e) {
      console.warn("[document] chemin de stockage inutilisable :", chemin, e?.code || e?.message);
      setDocPreviewUrl(null);
      setDocPreviewLoading(false);
      return;
    }
    setDocPreviewLoading(true);
    apercuReprisesRef.current.forEach(clearTimeout);
    const timers = [];
    apercuReprisesRef.current = timers;
    const arreterReprises = () => { timers.forEach(clearTimeout); timers.length = 0; };
    const INTERVALLE = 2000;
    const ABANDON = 60000;
    let ecoule = 0;

    const demander = () => {
      getDownloadURL(reference)
        .then((url) => {
          if (apercuCheminRef.current !== chemin) return;
          arreterReprises();
          setDocPreviewUrl(url);
          setDocPreviewLoading(false);
        })
        .catch((e) => {
          if (apercuCheminRef.current !== chemin) return;
          const pasEncore = e?.code === "storage/object-not-found";
          ecoule += INTERVALLE;
          // On insiste SEULEMENT sur un objet pas encore pose. Toute autre
          // erreur — droit refuse, chemin mort, reseau — est definitive :
          // rappeler la meme chose toutes les 2 s ne la resoudrait pas.
          if (!pasEncore || ecoule > ABANDON) {
            apercuCheminRef.current = null;
            setDocPreviewUrl(null);
            setDocPreviewLoading(false);
            return;
          }
          timers.push(setTimeout(demander, INTERVALLE));
        });
    };
    demander();
    return arreterReprises;
  }, [selectedDocId, data]);

  /*
    CLIQUER LE DOCUMENT « MUTUELLE » OUVRE SES DONNEES A GAUCHE.

    Nicolas, 18/09 : « Lorsqu'on clic sur le document mutuelle, ouvre
    automatiquement les donnees lues de la mutuelle dans la colonne a gauche
    pour pouvoir les clic/copie. »

    C'est le geste naturel : on regarde la photo de la carte a droite, et on
    veut les numeros a recopier a cote. Le pave etait replie par defaut et il
    fallait aller le chercher.
  */
  useEffect(() => {
    if (!selectedDocId || !data?.mutuelleResume) return;
    const docChoisi = (data.documents || []).find((d) => d.id === selectedDocId);
    if (docChoisi?.key !== "mutual") {
      /*
        ET ELLE SE REFERME QUAND ON PASSE A AUTRE CHOSE.

        Nicolas, 18/09 (18h18) : « Lorsqu'on clic sur l'image de la mutuelle on
        deplie bien mais quand on change de document l'encart ne se reduit
        pas. »

        Le pave s'ouvrait sur le document mutuelle et restait ouvert pour
        toujours, poussant l'ordonnance hors de l'ecran. Il suit maintenant le
        document regarde, dans les deux sens.
      */
      setMutuelleOuverte(false);
      return;
    }
    setMutuelleOuverte(true);
    /*
      ON NE FAIT PLUS DEFILER L'ECRAN, sauf si la carte est DANS la vue.

      01/10 au soir : quand le dossier ouvert etait long, l'arrivee du resume
      par Firestore faisait sauter la colonne de gauche jusqu'a la carte de
      mutuelle — pendant qu'on lisait autre chose. Le defilement automatique
      ne sert qu'a un cas : la carte est deja sous les yeux quand on clique
      la photo, et elle a besoin d'un coup de pouce pour se montrer en entier.
      Sinon, on la laisse ou elle est.
    */
    const carte = mutuelleRef.current;
    if (!carte) return;
    const cadre = carte.getBoundingClientRect();
    if (cadre.top < 0 || cadre.bottom > window.innerHeight) {
      carte.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedDocId, data?.mutuelleResume]);

  /*
    LA CARTE DE MUTUELLE SE FAIT LIRE QUAND ON OUVRE LE DOSSIER.

    ─────────────────────────────────────────────────────────────────────────
    CE QUI A CHANGE LE 01/10
    ─────────────────────────────────────────────────────────────────────────

    La tablette lisait la carte au moment de la photographier. Elle ne le fait
    plus : c'est le back-office qui la fait lire par le serveur, des que le
    secretariat ouvre le dossier. Deux raisons, dans cet ordre :

      — la secretaire est DEVANT la carte ouverte : elle voit tout de suite si
        la lecture a marche, et peut relancer d'un clic ;
      — la lecture ne se paie plus sur le temps de passage du preleveur.

    Le serveur ecrit le resultat sur le rendez-vous (`mutuelleResume`) : c'est
    l'ecoute Firestore deja en place qui l'affiche, on ne pose rien ici.

    Le DOSSIER, et non le rendez-vous choisi : le secretariat ouvre le dossier
    avant de le lire, et un dossier rouvert plus tard ne se relit pas
    (`mutuellesLanceesRef`, un Set, une fois par dossier et par session).
  */
  useEffect(() => {
    /*
      CHAQUE DOSSIER RETROUVE SON ETAT EN S'OUVRANT.

      L'etat est garde PAR DOSSIER (`mutuellesEchecsRef`) et non dans un seul
      `mutuelleLecture` partage : sans cela, rouvrir un dossier dont la lecture
      venait d'echouer n'affichait plus rien du tout — le Set ci-dessous
      l'empeche de relancer, et l'etat avait ete efface. Le dossier paraissait
      alors n'avoir jamais eu de carte.
    */
    dossierOuvertRef.current = selectedAppointmentId;
    clearTimeout(mutuelleRelanceTimerRef.current);
    setMutuelleLecture(
      selectedAppointmentId && mutuellesEchecsRef.current.has(selectedAppointmentId)
        ? "illisible"
        : null
    );
  }, [selectedAppointmentId]);

  /*
    LA FILE DE LECTURE : UNE LECTURE A LA FOIS, JAMAIS ATTENDUE.

    ─────────────────────────────────────────────────────────────────────────
    CE QUI A CHANGE LE 01/10 AU SOIR
    ─────────────────────────────────────────────────────────────────────────

    Le client : « il faut que la lecture soit deja faite quand j'ouvre le
    dossier ». Jusqu'ici elle ne partait qu'a l'ouverture, et il l'attendait.
    On met donc en file TOUS les dossiers de la liste chargee qui portent une
    photo de mutuelle jamais lue — la regle vit dans `Utils/lectureMutuelle`
    (`dossiersAMettreEnFile`), avec ses tests.

    TROIS CHOSES QUE CETTE FILE NE DOIT PAS FAIRE, et qui sont ecrites ici
    pour qu'on ne les « simplifie » pas :

      — une rafale. Trente lectures d'un coup, c'est trente appels payants au
        modele et un poste qui ne repond plus. UNE lecture en vol a la fois
        (`mutuelleEnVolRef`), la suivante attend son tour ;
      — bloquer l'ecran. Rien n'attend cette file : ni la liste, ni l'ouverture
        d'un dossier, ni un clic. Elle tourne derriere, et la seule trace qu'on
        en voit est l'etat de la carte du dossier ouvert ;
      — relire. Une fois par dossier et par session (`mutuellesLanceesRef`),
        et le serveur tient le meme compte de son cote : deux postes ouverts
        ne lisent pas deux fois (verrou `mutuelleLue.enCours`).
  */
  const mutuelleFileRef = useRef([]);
  const mutuelleEnVolRef = useRef(false);
  /*
    LES DOSSIERS DONT LA LECTURE EST EN ROUTE.

    C'est ce qui permet a la carte du dossier ouvert de dire « Lecture… »
    pendant que la file travaille : sans ce Set, un dossier qui a quitte la
    file (il est en vol) ne serait ni « en file » ni « a lancer », et sa carte
    ne rendrait RIEN — c'est-a-dire « pas de mutuelle », exactement ce qu'il
    ne faut pas dire.
  */
  const mutuellesEnCoursRef = useRef(new Set());
  // Les dossiers qu'on a deja repasses voir une fois sur un « en cours ».
  const mutuellesReprisesRef = useRef(new Set());

  /**
   * Lire un dossier de la file, et dire ce que ca a donne.
   *
   * L'etat affiche ne concerne QUE le dossier ouvert : une lecture qui reussit
   * pendant que la secretaire regarde ailleurs ne doit rien changer a l'ecran,
   * et Firestore lui rendra son resume quand elle y reviendra.
   */
  async function lireUnDossierEnFile(rdv) {
    const estOuvert = () => dossierOuvertRef.current === rdv.id;
    if (estOuvert()) setMutuelleLecture("lecture");
    mutuellesEnCoursRef.current.add(rdv.id);
    let reponse;
    try {
      reponse = await lireMutuelleDossier(rdv.id);
    } finally {
      mutuellesEnCoursRef.current.delete(rdv.id);
    }
    const etat = etatDepuisReponse(reponse);
    if (etat === "lue") {
      /*
        LE RESUME ARRIVE PAR FIRESTORE, PAS D'ICI.

        La reponse le porte aussi, mais on ne s'en sert pas : c'est le
        rendez-vous qui fait foi, et le prochain instantane le posera dans la
        liste — donc dans `data`, donc a l'ecran (voir l'effet de
        synchronisation). On ne touche donc pas a l'etat : la carte continuera
        de dire « Lecture… » les quelques centaines de millisecondes qui
        separent la reponse de l'instantane, et basculera toute seule des que
        le resume arrivera. Laisser « lecture » ne peut rien casser : des
        qu'un resume existe, la carte le montre et ignore l'etat.
      */
      return;
    }
    if (etat === "en_cours") {
      /*
        UN AUTRE POSTE (OU LA TABLETTE) TIENT LE VERROU.

        Ce n'est pas un echec, et on ne double pas l'appel : le serveur a
        repondu qu'une lecture etait en route, son resultat arrivera par
        Firestore. On repasse voir UNE fois dans 8 s, au cas ou le verrou
        serait en fait mort ; au-dela on s'arrete, sinon on paierait un appel
        toutes les 8 s pour rien.
      */
      if (estOuvert()) setMutuelleLecture("lecture");
      if (!mutuellesReprisesRef.current.has(rdv.id)) {
        clearTimeout(mutuelleRelanceTimerRef.current);
        mutuelleRelanceTimerRef.current = setTimeout(() => {
          if (dossierOuvertRef.current !== rdv.id) return;
          // On repasse par la FILE, pas directement : c'est elle qui garantit
          // qu'une seule lecture est en vol. On le note repris, pour qu'une
          // reponse encore « en cours » n'en declenche pas une troisieme.
          mutuellesReprisesRef.current.add(rdv.id);
          mutuelleFileRef.current.unshift(rdv);
          viderFileMutuelle();
        }, 8000);
      }
      return;
    }
    mutuellesEchecsRef.current.add(rdv.id);
    if (estOuvert()) setMutuelleLecture("illisible");
  }

  /** Vide la file, une lecture a la fois. Ne rend jamais la main a l'appelant. */
  async function viderFileMutuelle() {
    if (mutuelleEnVolRef.current) return;
    mutuelleEnVolRef.current = true;
    try {
      while (mutuelleFileRef.current.length) {
        const rdv = mutuelleFileRef.current.shift();
        await lireUnDossierEnFile(rdv);
      }
    } finally {
      mutuelleEnVolRef.current = false;
    }
  }

  /**
   * La mise en file, a chaque changement de la liste.
   *
   * Rien n'est attendu ici : on empile et on rend la main. La liste s'affiche
   * avec ou sans cette file.
   */
  useEffect(() => {
    /*
      LE SERVEUR LIT LES MUTUELLES EN FOND DEPUIS LE 02/10 (tâche planifiée
      /taches/lire-mutuelles, chaque minute). Le navigateur ne relit plus toute
      la liste : à chaque ouverture du back-office il lançait des dizaines de
      lectures de 10 à 25 s, et le dossier ouvert attendait derrière (Nicolas,
      « ma première connexion a été insupportable »). Seul le dossier ouvert
      demande encore sa lecture s'il n'en a pas (effet suivant).
    */
    if (!LECTURE_FILE_NAVIGATEUR) return;
    const aMettre = dossiersAMettreEnFile(appointments, mutuellesLanceesRef.current);
    if (!aMettre.length) return;
    aMettre.forEach((rdv) => {
      mutuellesLanceesRef.current.add(rdv.id);
      mutuelleFileRef.current.push(rdv);
    });
    viderFileMutuelle();
  }, [appointments]);

  /*
    LE DOSSIER OUVERT PASSE DEVANT.

    C'est le filet : si la file n'est pas encore arrivee a ce dossier, celui
    qu'on ouvre n'attend pas son tour — il remonte en tete et part tout de
    suite. C'est aussi ce qui remplace l'ancien lancement a l'ouverture, sans
    avoir deux lecteurs a la fois : la file reste le seul endroit qui appelle
    le serveur.
  */
  useEffect(() => {
    const rdv = appointments.find((a) => a.id === selectedAppointmentId);
    if (!rdv) return;
    /*
      UN ECHEC DEJA ENREGISTRE NE SE RELIT PAS TOUT SEUL, MAIS SE DIT.

      `mutuelleLue.echec` sur le rendez-vous est la memoire de la lecture : le
      serveur a deja essaye et n'y est pas arrive. On ne relance donc pas a
      chaque ouverture — mais on l'AFFICHE, avec le meme « Relire » que pour
      un echec du jour. Le rendre muet reviendrait a effacer le travail de la
      secretaire de la veille.
    */
    if (rdv.mutual && !rdv.mutuelleResume && rdv.mutuelleLue?.echec) {
      mutuellesEchecsRef.current.add(rdv.id);
      setMutuelleLecture("illisible");
      return;
    }
    // Deja partie, deja en file, ou en vol : elle est forcement devant nous,
    // la file ne relit jamais un dossier qu'elle a pris.
    const dejaPrise =
      mutuelleFileRef.current.some((r) => r.id === rdv.id) ||
      mutuellesEnCoursRef.current.has(rdv.id);
    if (dejaPrise) {
      // Si c'est une AUTRE lecture qui est en vol devant nous, on dit
      // « Lecture… » : c'est la queue de la file, pas un dossier muet.
      if (dossierOuvertRef.current === rdv.id) {
        setMutuelleLecture("lecture");
        viderFileMutuelle();
      }
      return;
    }
    if (!fautLancerLecture(rdv, mutuellesLanceesRef.current)) return;
    mutuellesLanceesRef.current.add(rdv.id);
    mutuelleFileRef.current.unshift(rdv);
    setMutuelleLecture("lecture");
    viderFileMutuelle();
  }, [selectedAppointmentId, appointments]);

  // Load RPPS SIL code when prescripteur changes
  useEffect(() => {
    setPrescripteurSilCode(null);
    setEditPrescripteurSil(false);
    loadPrescripteurSilCode(data?.prescripteurInfo?.rpps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.prescripteurInfo?.rpps]);

  // ─── Handlers ───
  /** Demande de changer de dossier : passe par la confirmation si besoin. */
  function demanderChangementDossier(appointment) {
    if (appointment.id === selectedAppointmentId) return;
    if (dossierNonConclu()) {
      setDossierEnAttente(appointment);
      return;
    }
    handleSelectAppointment(appointment);
  }

  function handleSelectAppointment(appointment) {
    setDossierTravaille(false);
    setDossierEnAttente(null);
    setHistoOuvert(false);
    const motLaisse = appointment.commentaireSecretaire || "";
    setCommentaire(motLaisse);
    setCommentaireEnregistre(motLaisse);
    const roomName = roomNames[appointment.roomId] || "";
    const viewData = transformAppointmentToViewData(appointment, roomName);
    // L'effet de synchronisation compare les rendez-vous pour eviter un
    // recalcul inutile : il doit savoir lequel est a l'ecran.
    rendezVousAfficheRef.current = appointment;
    setData(viewData);
    setSelectedAppointmentId(appointment.id);
    setSelectedDocId(viewData.documents[0]?.id || null);
    setEditOpen(false);
    setEditBarcode(false);
    apercuCheminRef.current = null;
    setDocPreviewUrl(null);
    setInNir(viewData.identity.nir || "");
    setInBirthCity(viewData.identity.birthCityCode || "");
    setInDossierBarcode(viewData.dossierBarcode || "");
  }

  /*
    UNE IMAGE QUI N'ARRIVE PAS SE RE-DEMANDE UNE FOIS.

    Le `getDownloadURL` de l'effet a beau reussir — l'objet existe, l'URL est
    signee — la photo elle-meme peut encore echouer au chargement : URL expiree
    entre-temps, coupure reseau, ou objet remplace par la tablette. La vignette
    restait alors cassee jusqu'au rechargement.

    On redemande donc l'URL une fois, et une seule : au-dela, c'est que le
    probleme n'est pas l'URL, et insister ferait clignoter l'image.
  */
  function relancerApercuUneFois() {
    const chemin = apercuCheminRef.current || selectedDoc?.storagePath;
    if (!chemin) return;
    let reference;
    try {
      reference = ref(getStorage(), chemin);
    } catch (e) {
      return;
    }
    getDownloadURL(reference)
      .then((url) => { if (apercuCheminRef.current === chemin) setDocPreviewUrl(url); })
      .catch(() => { /* l'effet reprend la main si le chemin est encore affiche */ });
  }

  /** Un geste de travail sur le dossier en cours. */
  function marquerTravail() {
    if (selectedAppointmentId) setDossierTravaille(true);
  }

  /** Vrai si le dossier ouvert a ete travaille sans avoir ete conclu. */
  function dossierNonConclu() {
    if (!selectedAppointmentId || !dossierTravaille) return false;
    // « À vérifier » conclut le dossier autant que « traité » : la secrétaire
    // a dit ce qu'elle avait à en dire, elle passe la main.
    return statutCourant() === STATUT_NOUVEAU;
  }

  function toast(msg) {
    setToastMsg(msg);
    setToastVisible(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 1800);
  }

  /*
    LE RESUME PDF DU DOSSIER, DEPUIS LE SECRETARIAT.

    Nicolas, 18/09 : « Pour le resume PDF, je veux que tu mettes les 2 boutons
    une ligne au dessus de l'ensemble des autres documents avec les mots
    "Telecharger le resume PDF" / "Afficher le resume PDF" », et sur ma
    synthese de la veille : « non pas completement et pas bien affiche ».

    Il avait raison sur les deux points. Ce qui existait ici agissait sur le
    DOCUMENT AFFICHE (telecharger cette ordonnance en PDF), pas sur le resume
    du dossier — lequel n'etait joignable que depuis « Derniers RDV ». Or
    c'est au secretariat qu'on en a besoin : c'est la qu'on saisit dans le
    SIL. Les deux boutons vivent donc ici, sur leur propre ligne, au-dessus
    des documents, avec les mots qu'il a demandes.

    Le nom du fichier suit la meme regle que partout ailleurs
    (`Utils/documentNaming`), pour que le SIL retrouve ses petits.
  */
  const [resumeEnCours, setResumeEnCours] = useState(false);

  function nomDuResume() {
    const secondes =
      dossierCourant()?.ended_at?.seconds ||
      dossierCourant()?.started_at?.seconds ||
      Math.floor(Date.now() / 1000);
    return nomFichierResume({
      barcode: data?.dossierBarcode,
      date: moment.unix(secondes).format("DD_MM_YYYY"),
      firstName: data?.identity?.firstNamesUsed,
      lastNameUsed: data?.identity?.lastNameUsed,
      lastNameBirth: data?.identity?.lastNameBirth,
    });
  }

  /** Le resume, rapatrie du serveur. `null` si le PDF revient vide. */
  async function recupererResume() {
    const reponse = await ExpressAPI(
      "GET",
      `pdf/getAppointment/${selectedAppointmentId}`,
      [],
      "blob"
    );
    const fichier = new Blob([reponse.data], { type: "application/pdf" });
    return fichier.size ? fichier : null;
  }

  async function afficherResumePdf() {
    if (!selectedAppointmentId) return;
    // L'onglet s'ouvre AVANT l'appel reseau, tant qu'on est encore dans le
    // clic : un `window.open` apres un `await` est bloque par Chrome.
    const onglet = window.open("", "_blank");
    try {
      setResumeEnCours(true);
      const fichier = await recupererResume();
      if (!fichier) {
        onglet?.close();
        toast("Le résumé est vide");
        return;
      }
      const url = URL.createObjectURL(fichier);
      if (onglet) onglet.location = url;
      else window.open(url, "_blank");
      marquerTravail();
    } catch (e) {
      onglet?.close();
      toast(e?.response?.status === 404 ? "Dossier introuvable" : "Résumé PDF impossible");
    } finally {
      setResumeEnCours(false);
    }
  }

  async function telechargerResumePdf() {
    if (!selectedAppointmentId) return;
    try {
      setResumeEnCours(true);
      const fichier = await recupererResume();
      if (!fichier) {
        toast("Le résumé est vide");
        return;
      }
      const url = URL.createObjectURL(fichier);
      const lien = document.createElement("a");
      lien.href = url;
      lien.setAttribute("download", nomDuResume());
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      marquerTravail();
    } catch (e) {
      toast(e?.response?.status === 404 ? "Dossier introuvable" : "Résumé PDF impossible");
    } finally {
      setResumeEnCours(false);
    }
  }

  /*
    « RELIRE » : la secretaire VOIT la carte, et elle n'a pas ete lue.

    01/10. L'echec n'est pas forcement definitif — une photo prise de biais,
    une carte qu'on retourne, un contraste qu'on corrige — et c'est
    precisement parce que la secretaire a la carte sous les yeux qu'elle sait
    si ca vaut le coup. Le bouton relance avec `force: true` : le serveur
    refait la lecture sans tenir compte de la sienne.

    `mutuellesLanceesRef` n'est PAS consulte ici : c'est justement le geste
    qui dit « recommence ».
  */
  async function relireMutuelle() {
    if (!selectedAppointmentId) return;
    const id = selectedAppointmentId;
    setMutuelleLecture("lecture");
    const reponse = await lireMutuelleDossier(id, { force: true });
    // Le dossier a pu changer pendant l'appel : on ne pose rien sur le nouveau.
    if (selectedAppointmentId !== id) return;
    const etat = etatDepuisReponse(reponse);
    if (etat === "lue") {
      mutuellesEchecsRef.current.delete(id);
      setMutuelleLecture(null);
      return;
    }
    if (etat === "en_cours") {
      // Deja en route cote serveur : le resultat arrivera par Firestore. On
      // laisse la lecture en cours a l'ecran sans promettre d'echec.
      return;
    }
    mutuellesEchecsRef.current.add(id);
    setMutuelleLecture("illisible");
  }

  async function copyToClipboard(text) {
    const v = texteACopier(text);
    if (!v) { toast("Rien à copier"); return; }
    // Le presse-papiers recoit le texte ENTIER, sauts de ligne compris.
    try { await navigator.clipboard.writeText(v); }
    catch (e) {
      const ta = document.createElement("textarea");
      ta.value = v; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); ta.remove();
    }
    marquerTravail();
    toast(messageCopie(v));
  }

  // Retour du 28/08 : ce nom differait de celui de « Dernier RDV » — ordre
  // inverse, pas de numero de dossier, et surtout le nom de NAISSANCE, si bien
  // qu un patient n ayant qu un nom d usage ressortait sans nom. On applique
  // desormais exactement la meme regle que « Dernier RDV ».
  /**
   * Le dossier vient d'etre cree et ses photos ne sont pas encore arrivees.
   *
   * La tablette cree le dossier, puis ecrit les chemins des photos ~8 s plus
   * tard. Pendant cette fenetre — on se donne 10 minutes, large pour un envoi
   * qui traine — un dossier sans document ATTEND quelque chose. Au-dela, il
   * n'attend plus rien : c'est un dossier sans document, et le dire
   * autrement serait mentir a la secretaire.
   */
  function photosEnRoute() {
    const debut = heurePriseEnCharge(dossierCourant());
    if (!debut?.seconds) return true; // pas d'heure connue : on ne conclut pas
    return Date.now() / 1000 - debut.seconds < 600;
  }

  /**
   * Le nom du fichier telecharge. `extension` decide seule du suffixe : la
   * regle de nommage, elle, ne change pas d'un format a l'autre — c'est
   * exactement ce qu'on avait corrige le 28/08 en sortant cette regle dans
   * `Utils/documentNaming`, et le PDF ne doit pas rouvrir la breche.
   */
  function getFormattedDownloadName(docItem, extension = "jpg") {
    if (!data) return `document.${extension}`;
    return nomFichierDocument({
      barcode: data.dossierBarcode,
      jour: data.jourDocument,
      type: (typeof docItem === "string" ? docItem : docItem?.typeFichier) || "document",
      firstName: data.identity.firstNamesUsed,
      lastNameUsed: data.identity.lastNameUsed,
      lastNameBirth: data.identity.lastNameBirth,
      extension,
    });
  }

  /** Pose un blob dans les telechargements du poste, sous le nom voulu. */
  function enregistrerBlob(blob, nomFichier) {
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", nomFichier);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    // Sans cela, chaque telechargement laisse l'image en memoire jusqu'a la
    // fermeture de l'onglet — et une secretaire ne ferme pas son onglet.
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  }

  /** Le document tel qu'il est stocke, sans conversion. */
  async function lireDocument(storagePath) {
    const url = await getDownloadURL(ref(getStorage(), storagePath));
    const reponse = await fetch(url);
    if (!reponse.ok) throw new Error(`Lecture refusée (${reponse.status})`);
    return await reponse.blob();
  }

  async function downloadDocument(storagePath, docItem) {
    try {
      const blob = await lireDocument(storagePath);
      enregistrerBlob(blob, getFormattedDownloadName(docItem, "jpg"));
      marquerTravail();
      toast("Téléchargement lancé");
    } catch (e) {
      console.error(e);
      toast("Erreur de téléchargement");
    }
  }

  /**
   * Le meme document, converti en PDF.
   *
   * Retour du 08/09 : « le SIL ne semble accepter que les PDF ». La conversion
   * vit dans `Utils/documentPdf` ; ici on ne fait que la declencher et dire ce
   * qui se passe.
   *
   * L'echec est ANNONCE et non avale : un PDF qui ne part pas se voit tout de
   * suite, un PDF vide qui part se decouvre au SIL, trois jours plus tard.
   */
  async function downloadDocumentPdf(storagePath, docItem) {
    if (pdfEnCours) return;
    try {
      setPdfEnCours(true);
      toast("Conversion en PDF…");
      const blob = await lireDocument(storagePath);
      const pdf = await documentEnPdf(blob);
      enregistrerBlob(pdf, getFormattedDownloadName(docItem, "pdf"));
      marquerTravail();
      toast("PDF téléchargé");
    } catch (e) {
      console.error(e);
      toast(e?.message || "La conversion en PDF a échoué");
    } finally {
      setPdfEnCours(false);
    }
  }

  /** Le dossier ouvert, tel qu'il est en memoire. */
  function dossierCourant() {
    return appointments.find((a) => a.id === selectedAppointmentId) || null;
  }

  /** Le statut du dossier ouvert. */
  function statutCourant() {
    return statutDossier(dossierCourant());
  }

  /**
   * Pose un statut sur le dossier ouvert.
   *
   * Les trois etats passent par ici, y compris le retour en arriere : Nicolas
   * demande le 04/09 de pouvoir recliquer « Dossier deja traite » pour rendre
   * au dossier son statut initial. Un geste qu'on ne peut pas defaire est un
   * geste qu'on hesite a faire.
   */
  async function poserStatut(statut) {
    if (!selectedAppointmentId) return;
    const champs = champsPourStatut(statut);
    try {
      const docRef = doc(firebase.db, "appointements", selectedAppointmentId);
      await updateDoc(docRef, {
        ...champs,
        // On ne date que ce qui vient d'arriver : l'heure du « traite »
        // repond a « quand ? », et elle n'aurait pas de sens sur un retour
        // en arriere.
        ...(statut === STATUT_TRAITE ? { seenAt: serverTimestamp() } : {}),
        statutPosePar: moi.nom || moi.email,
        statutPoseA: serverTimestamp(),
        // Retour du 04/09 (17h41) : « garde bien l'historique des differents
        // statuts ». On empile plutot que d'ecraser -- c'est le seul moyen de
        // repondre plus tard a « qui a remis ce dossier a verifier, et
        // quand ? ». L'horodatage est pose ici et non par le serveur :
        // serverTimestamp() n'est pas autorise a l'interieur d'un tableau.
        historiqueStatuts: arrayUnion({
          statut,
          par: moi.nom || moi.email,
          le: new Date().toISOString(),
        }),
      });
      const snap = await getDoc(docRef);
      if (snap.exists() && statutDossier(snap.data()) === statut) {
        const trace = {
          statut,
          par: moi.nom || moi.email,
          le: new Date().toISOString(),
        };
        setAppointments((prev) =>
          prev.map((a) =>
            a.id === selectedAppointmentId
              ? { ...a, ...champs, historiqueStatuts: [...(a.historiqueStatuts || []), trace] }
              : a
          )
        );
        if (statut !== STATUT_NOUVEAU) setDossierTravaille(false);
        toast(
          statut === STATUT_TRAITE
            ? "Dossier marqué comme traité !"
            : statut === STATUT_A_VERIFIER
              ? "Dossier signalé « À vérifier »"
              : "Dossier remis à son statut initial"
        );
      } else {
        console.error("Firestore write may not have persisted - statut:", snap.data()?.statut);
        toast("Erreur : la mise à jour n'a pas été enregistrée");
      }
    } catch (e) {
      console.error("poserStatut error:", e);
      toast("Erreur : " + (e.message || "mise à jour impossible"));
    }
  }

  /**
   * « Dossier cree ».
   *
   * Retour du 04/09 : cliquer sans avoir rien fait sur le dossier merite une
   * question. C'est la symetrique de l'autre alerte — on demande confirmation
   * quand on part SANS avoir marque, on la demande aussi quand on marque SANS
   * avoir travaille. Dans les deux cas, ce qu'on protege est la meme chose :
   * un dossier dont l'etat affiche ne correspond pas a ce qui s'est passe.
   */
  async function handleDossierCree() {
    if (!selectedAppointmentId) return;
    if (!dossierTravaille) {
      setCreationADemander(true);
      return;
    }
    await poserStatut(STATUT_TRAITE);
  }

  /** Enregistre le mot laisse a la collegue suivante. */
  async function enregistrerCommentaire() {
    if (!selectedAppointmentId) return;
    const texte = commentaire.trim();
    if (texte === commentaireEnregistre.trim()) return;
    try {
      await updateDoc(doc(firebase.db, "appointements", selectedAppointmentId), {
        commentaireSecretaire: texte,
        // Le NOM, pas l'adresse (retour du 04/09, 17h41). L'adresse reste
        // enregistree a cote : elle sert a la tracabilite, pas a l'affichage.
        commentaireSecretaireAuteur: moi.nom || moi.email,
        commentaireSecretaireEmail: moi.email,
        commentaireSecretaireA: serverTimestamp(),
      });
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === selectedAppointmentId
            ? { ...a, commentaireSecretaire: texte, commentaireSecretaireAuteur: moi.nom || moi.email }
            : a
        )
      );
      setCommentaireEnregistre(texte);
      toast(texte ? "Commentaire enregistré" : "Commentaire effacé");
    } catch (e) {
      console.error("enregistrerCommentaire error:", e);
      toast("Erreur : commentaire non enregistré");
    }
  }

  function notAvailable() {
    toast("Fonctionnalité bientôt disponible");
  }

  function sglNotAvailable() {
    toast("Votre laboratoire n'accepte pas encore la réception des fichiers dans votre SGL");
  }

  async function saveBarcodeFromHeader() {
    const val = inDossierBarcode.trim();
    if (!selectedAppointmentId) { setEditBarcode(false); return; }
    if (!val) { toast("Le N° dossier ne peut pas être vide"); return; }
    try {
      await updateDoc(doc(firebase.db, "appointements", selectedAppointmentId), { barcode: val });
      // B1 : synchroniser le cache local des RDV pour que le N° persiste au re-clic (saisie manuelle)
      setAppointments((prev) => prev.map((a) => (a.id === selectedAppointmentId ? { ...a, barcode: val } : a)));
      setData((prev) => ({ ...prev, dossierBarcode: val }));
      setEditBarcode(false);
      toast("N° dossier enregistré !");
    } catch (e) {
      toast("Erreur sauvegarde dossier");
    }
  }

  // ─── RPPS → SIL code mapping ───
  async function loadPrescripteurSilCode(rpps) {
    if (!rpps) { setPrescripteurSilCode(null); return; }
    const labId = localStorage.getItem("currentLab");
    if (!labId) return;
    try {
      const d = await getDoc(doc(firebase.db, "laboratories", labId, "prescripteurs", rpps));
      if (d.exists()) {
        setPrescripteurSilCode(d.data().silCode || null);
        setInPrescripteurSilCode(d.data().silCode || "");
      } else {
        setPrescripteurSilCode(null);
        setInPrescripteurSilCode("");
      }
    } catch (e) { /* ignore */ }
  }

  async function savePrescripteurSilCode() {
    const rpps = data?.prescripteurInfo?.rpps;
    if (!rpps) return;
    const labId = localStorage.getItem("currentLab");
    if (!labId) return;
    const val = inPrescripteurSilCode.trim();
    try {
      await setDoc(doc(firebase.db, "laboratories", labId, "prescripteurs", rpps), {
        rpps,
        nom: data.prescripteurInfo.nom || "",
        prenom: data.prescripteurInfo.prenom || "",
        specialite: data.prescripteurInfo.specialite || "",
        silCode: val,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setPrescripteurSilCode(val || null);
      setEditPrescripteurSil(false);
      toast(val ? "Code SIL prescripteur enregistré !" : "Code SIL retiré");
    } catch (e) {
      toast("Erreur sauvegarde code SIL");
    }
  }

  // ─── Derived values ───
  // La recherche, puis le tri choisi (heure / salle / Mode direct). Le tri vit
  // dans Utils/triDossiers : il ordonne par heure de prise en charge, range par
  // salle, et « Mode direct » FILTRE au lieu de trier (Nicolas, 13/09).
  const filteredAppointments = trierDossiers(
    appointments.filter((appt) => {
      const fullName = `${appt.name || ""} ${appt.lastName || ""} ${appt.maritalName || ""}`.toLowerCase();
      return fullName.includes(searchQuery.trim().toLowerCase());
    }),
    triListe,
    roomNames
  );
  const seenCount = appointments.filter((a) => statutDossier(a) === STATUT_TRAITE).length;
  const selectedDoc = data?.documents.find((d) => d.id === selectedDocId);

  // ─── Field renderer ───
  // La CNI, le passeport et la e-ordo ont leur propre pastille depuis le
  // 16/09 : tout ne vient plus du Datamatrix INS. Le violet dit « lu par une
  // machine », le bleu reste la saisie humaine (Utils/sourceCode).
  const sourceClass = classeSourceCode;
  /**
   * Une tuile du pave d'identite, avec son avertissement de format.
   *
   * Retour du 08/09, capture a l'appui : « Telephone 07876 », « Email test ».
   * Ces valeurs traversaient la tablette, le reseau et Firestore pour
   * s'afficher ici avec le meme aplomb qu'une valeur juste, et repartir telles
   * quelles vers le SIL.
   *
   * `champ` est la cle de la regle a appliquer (voir Utils/formatValeurs).
   * Une tuile sans `champ`, ou dont le champ n'a pas de regle, ne signale
   * jamais rien : c'est le cas des noms et des prenoms, volontairement.
   *
   * Le clic continue de copier la valeur TELLE QUELLE, hors format compris.
   * Un numero etrange peut etre le bon numero, et la secretaire a parfois
   * besoin de le recopier pour aller le verifier ailleurs.
   */
  function FieldButton({ label, value, source, champ }) {
    const alerte = messageFormat(champ, value);
    return (
      <button
        className={`sb-copyBtn${alerte ? " sb-horsFormat" : ""}`}
        type="button"
        onClick={() => copyToClipboard(String(value || ""))}
      >
        <div className="sb-k">
          {label}
          {source && <span className={`sb-src ${sourceClass(source)}`}>{source}</span>}
          {alerte && <span className="sb-alerteFormat" aria-hidden="true">!</span>}
        </div>
        <div className="sb-v"><span>{value || "—"}</span></div>
        {alerte && <div className="sb-msgFormat">{alerte}</div>}
      </button>
    );
  }

  function NirField({ value, source }) {
    const full15 = nirAvecCle(value);
    const sans13 = nirSansCle(value);
    // Le NIR est le seul champ de ce pave capable de dire « ce numero est
    // FAUX » plutot que « ce numero est etrange » : sa cle de controle se
    // recalcule. L'alerte se pose sur la tuile a quinze chiffres, la seule
    // qui la porte.
    const alerteNir = messageFormat("nir", full15);
    return (
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="sb-copyBtn"
          style={{ flex: 1, minWidth: 180, textAlign: "left", border: `1px solid ${alerteNir ? "#f5c2be" : "var(--line)"}`, borderRadius: 10, padding: "8px 10px", background: alerteNir ? "#fffafa" : "#fbfdff", cursor: full15.length >= 13 ? "pointer" : "default" }}
          onClick={() => full15 && copyToClipboard(full15)}
        >
          <div className="sb-k">
            NIR avec clé
            {source && <span className={`sb-src ${sourceClass(source)}`}>{source}</span>}
            {alerteNir && <span className="sb-alerteFormat" aria-hidden="true">!</span>}
          </div>
          <div className="sb-v" style={{ fontFamily: "monospace", fontSize: 13, letterSpacing: "0.5px" }}>{full15 || "—"}</div>
          {alerteNir && <div className="sb-msgFormat">{alerteNir}</div>}
        </button>
        <button
          type="button"
          className="sb-copyBtn"
          style={{ flex: 1, minWidth: 180, textAlign: "left", border: "1px solid var(--line)", borderRadius: 10, padding: "8px 10px", background: "#fbfdff", cursor: sans13.length >= 13 ? "pointer" : "default" }}
          onClick={() => sans13 && copyToClipboard(sans13)}
        >
          <div className="sb-k">NIR sans clé</div>
          <div className="sb-v" style={{ fontFamily: "monospace", fontSize: 13, letterSpacing: "0.5px" }}>{sans13 || "—"}</div>
        </button>
      </div>
    );
  }

  function docStatusLabel(s) {
    if (s === "INTEGRATED") return "Intégré";
    if (s === "SENT") return "Envoyé";
    return "Non envoyé";
  }
  function docStatusClass(s) {
    if (s === "INTEGRATED") return "int";
    if (s === "SENT") return "sent";
    return "not";
  }

  // ─── Render ───
  return (
    <>
      <style>{CSS}</style>
      <div className="sb-root">
        <div className="sb-layout">
          {/* SIDEBAR */}
          <div className="sb-sidebar">
            <div className="sb-sidebarHeader">
              <div className="sb-sidebarTitle">
                <span className="sb-icon">&#x1F4CB;</span>
                Dossiers
              </div>
              <input
                className="sb-sidebarSearch"
                placeholder="Rechercher un patient..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {!isLoading && appointments.length > 0 && (
                <div className="sb-sidebarStats">
                  <span className="sb-pill sb-mini">{appointments.length} total</span>
                  {seenCount > 0 && <span className="sb-pill sb-ok sb-mini">{seenCount} traité{seenCount > 1 ? "s" : ""}</span>}
                  {appointments.length - seenCount > 0 && <span className="sb-pill sb-warn sb-mini">{appointments.length - seenCount} en attente</span>}
                </div>
              )}
              {/* Les trois pastilles de tri (Nicolas, 13/09) : par heure de
                  prise en charge, par salle, ou le filtre Mode direct. */}
              <div className="sb-sortRow">
                <button type="button" className={`sb-sortBtn ${triListe === "heure" ? "sb-on" : ""}`} onClick={() => setTriListe("heure")}>Par heure</button>
                <button type="button" className={`sb-sortBtn ${triListe === "salle" ? "sb-on" : ""}`} onClick={() => setTriListe("salle")}>Par salle</button>
                <button type="button" className={`sb-sortBtn ${triListe === "direct" ? "sb-on" : ""}`} onClick={() => setTriListe("direct")}>Mode direct</button>
              </div>
            </div>
            <div className="sb-sidebarList">
              {isLoading ? (
                <div className="sb-loading"><div className="sb-spinner" /><span style={{ fontSize: 11 }}>Chargement...</span></div>
              ) : filteredAppointments.length === 0 ? (
                <div style={{ textAlign: "center", padding: 16, color: "var(--muted)", fontSize: 11 }}>
                  {appointments.length === 0 ? "Aucun dossier (8 derniers jours)" : triListe === "direct" ? "Aucun dossier en Mode direct" : "Aucun résultat"}
                </div>
              ) : (
                filteredAppointments.map((appt) => (
                  <div key={appt.id} className={`sb-apptItem ${selectedAppointmentId === appt.id ? "sb-selected" : ""} ${statutDossier(appt) === STATUT_TRAITE ? "sb-seen" : ""} ${statutDossier(appt) === STATUT_A_VERIFIER ? "sb-aVerifier" : ""} ${appt.modeDirect ? "sb-directItem" : ""}`} onClick={() => demanderChangementDossier(appt)}>
                    <div className="sb-apptName">{appt.maritalName || appt.lastName} {appt.name}</div>
                    <div className="sb-apptMeta">
                      {/* L'heure affichee est celle de la PRISE EN CHARGE, pas
                          celle de la fin du rendez-vous (Nicolas, 13/09) ; les
                          dossiers anciens, sans cette heure, gardent leur date. */}
                      {(() => {
                        const h = heurePriseEnCharge(appt);
                        return h?.seconds ? moment.unix(h.seconds).format("DD/MM/YY HH:mm") : "—";
                      })()}
                      {appt.birthday ? ` · ${appt.birthday}` : ""}
                      {` · ${nomLieu(appt, roomNames)}`}
                    </div>
                    <div className="sb-apptBadges">
                      {appt.modeDirect && <span className="sb-pill sb-direct">Mode direct</span>}
                      {statutDossier(appt) === STATUT_TRAITE && <span className="sb-pill sb-ok sb-mini">Traité</span>}
                      {statutDossier(appt) === STATUT_A_VERIFIER && <span className="sb-pill sb-pillAVerifier sb-mini">À vérifier</span>}
                      {appt.commentaireSecretaire && <span className="sb-pill sb-mini" title={appt.commentaireSecretaire}>💬</span>}
                      {/* Retour du 04/09 : la tuile n'annonçait que la
                          première ordonnance. Un dossier qui porte une Ordo 3
                          ou une pièce d'identité le disait nulle part. Chaque
                          document présent a maintenant sa pastille. */}
                      {DOCS_TUILE.filter((d) => appt[d.key]).map((d) => (
                        <span key={d.key} className="sb-pill sb-mini">{d.court}</span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* MAIN CONTENT */}
          {/* Le filet : un dossier qui ne sait pas s'afficher ne doit pas
              emporter le back-office avec lui (Nicolas, 04/10 : trois dossiers
              Mode direct de la tablette 3.3 donnaient une page blanche, il
              fallait recharger). Voir Elements/Filet.jsx. */}
          <Filet
            zone="secretariat/dossier"
            titre="Ce dossier n'a pas pu s'afficher"
            cle={selectedAppointmentId}
            onReessayer={() => { setSelectedAppointmentId(null); setData(null); }}
          >
          <div className="sb-mainContent">
            {!data ? (
              <div className="sb-emptySelect">
                <div className="sb-emptyIcon">&#x1F4C2;</div>
                <div className="sb-emptyTitle">Sélectionnez un dossier</div>
                <div className="sb-emptySubtitle">Cliquez sur un patient dans la liste</div>
              </div>
            ) : (
              <div className="sb-app">
                {/* HEADER */}
                {/* Retour du 04/09 (17h41) : vert quand le dossier est
                    traité, violet quand il est à vérifier. « Comme ça on voit
                    très vite le statut » — et c'est le bon endroit, ce bandeau
                    est la première chose que l'oeil trouve. */}
                <div className={`sb-header ${
                  statutCourant() === STATUT_TRAITE ? "sb-hTraite" :
                  statutCourant() === STATUT_A_VERIFIER ? "sb-hAVerifier" : ""
                }`}>
                  <div className="sb-hTop">
                    <div>
                      <div className="sb-hTitle">{data.identity.lastNameUsed || data.identity.lastNameBirth} {data.identity.firstNamesUsed} — {data.identity.dob}</div>
                      <div className="sb-hSub">
                        {data.modeDirect && <div className="sb-chip sb-chipDirect"><span className="sb-dot" /><span>Mode direct</span></div>}
                        {data.room && <div className="sb-chip" onClick={() => copyToClipboard(data.room)}><span className="sb-dot" /><span>{data.room}</span></div>}
                        {data.createdAt && <div className="sb-chip" onClick={() => copyToClipboard(data.createdAt)}><span className="sb-dot" /><span>Prise en charge {data.createdAt}</span></div>}
                        {data.collector && <div className="sb-chip" onClick={() => copyToClipboard(data.collector)}><span className="sb-dot" /><span>Par: {data.collector}</span></div>}
                        {data.prescripteurInfo?.nom && (
                          <div className="sb-chip" onClick={() => copyToClipboard(`Dr ${data.prescripteurInfo.nom} ${data.prescripteurInfo.prenom || ""} ${data.prescripteurInfo.rpps ? `(RPPS: ${data.prescripteurInfo.rpps})` : ""}`.trim())}>
                            <span className="sb-dot" /><span>Dr {data.prescripteurInfo.nom}</span>
                          </div>
                        )}
                        {/* DÉSACTIVÉ — confiance IA masquée tant que l'IA n'est pas activée
                        {data.confidence != null && <div className="sb-chip"><span className="sb-dot" /><span>IA: {Math.round(data.confidence * 100)}%</span></div>}
                        */}
                      </div>
                    </div>
                    {editBarcode ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <input
                          value={inDossierBarcode}
                          onChange={(e) => setInDossierBarcode(e.target.value)}
                          placeholder="N° dossier"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === "Enter") saveBarcodeFromHeader(); if (e.key === "Escape") setEditBarcode(false); }}
                          style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8, padding: "4px 8px", color: "#fff", fontSize: 13, fontWeight: 800, fontFamily: "monospace", width: 160 }}
                        />
                        <button onClick={saveBarcodeFromHeader} style={{ padding: "3px 9px", borderRadius: 6, border: "none", background: "rgba(255,255,255,0.3)", color: "#fff", fontWeight: 800, fontSize: 11, cursor: "pointer" }}>OK</button>
                        <button onClick={() => setEditBarcode(false)} style={{ padding: "3px 7px", borderRadius: 6, border: "none", background: "rgba(255,255,255,0.1)", color: "#fff", fontWeight: 800, fontSize: 11, cursor: "pointer" }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="sb-barcodeWrap" onClick={() => copyToClipboard(data.dossierBarcode || data.id)} title="Cliquer pour copier le numéro de dossier">
                          <svg ref={barcodeRef} />
                          <span className="sb-barcodeId">Dossier: <b>{data.dossierBarcode || <span style={{opacity:0.4,fontStyle:"italic"}}>non défini</span>}</b></span>
                        </div>
                        <button
                          onClick={() => { setInDossierBarcode(data.dossierBarcode || ""); setEditBarcode(true); }}
                          title="Modifier le N° dossier"
                          style={{ padding: "3px 7px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 15, cursor: "pointer", lineHeight: 1, flexShrink: 0 }}
                        >✎</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* CONTENT */}
                <div className="sb-content">
                  {/* LEFT COL */}
                  <div className="sb-col">
                    {/* Verification greffee : invisible quand le dossier est
                        propre, lisere rouge et phrase de Lybo sinon. */}
                    <VerificationDossier
                      appointmentId={selectedAppointmentId}
                      onTravail={marquerTravail}
                    />

                    {/* Les discordances d'identite, quand les documents (Data
                        Matrix, CNI, ordo, mutuelle) ne disent pas la meme
                        chose : la secretaire choisit la valeur a garder.
                        Invisible quand tout le monde est d'accord. */}
                    <ChoixIdentite
                      appointment={appointments.find((a) => a.id === selectedAppointmentId)}
                      appointmentId={selectedAppointmentId}
                      firebase={firebase}
                      onTravail={marquerTravail}
                    />

                    {/* Identity */}
                    <div className="sb-card">
                      <h3><span className="sb-icon">&#x1F464;</span> Identité (clic = copie)</h3>
                      {/* Retour du 04/09 : le bouton vit ici, juste sous le
                          titre, et l'attestation biométrique a disparu. Il
                          n'apparaît que si un Datamatrix a réellement été
                          scanné : sur un dossier sans scan, il n'aurait rien
                          à montrer. */}
                      {data.insReading && (
                        <div className="sb-topCopyRow">
                          <button
                            className="sb-btnMini sb-blue"
                            onClick={() => setCodeLuOuvert(true)}
                          >
                            {titreSourceCode(data.insReading.source)} — Voir le contenu du Data Matrix
                            {data.insReading.anomalies?.length ? " ⚠" : ""}
                          </button>
                        </div>
                      )}
                      {/*
                        Pas de chips de mutuelle ici : essayees le 01/10 au
                        soir, retirees le meme soir a la demande du client
                        (« pas besoin des chips en haut, je me suis fait mal
                        comprendre »). La mutuelle vit dans sa carte, plus bas.
                      */}
                      <NirField value={data.identity.nir} source={data.identitySources?.nir} />
                      <div className="sb-grid3">
                        <FieldButton label="Nom utilisé" value={data.identity.lastNameUsed} source={data.identitySources?.lastNameUsed} />
                        <FieldButton label="Nom naissance" value={data.identity.lastNameBirth} source={data.identitySources?.lastNameBirth} />
                        <FieldButton label="Prénom (usage)" value={data.identity.firstNamesUsed} source={data.identitySources?.firstNamesUsed} />
                        {/* Retour du 02/09 : les prénoms de naissance au complet,
                            que la tablette n'affiche plus mais garde en mémoire. */}
                        <FieldButton label="Prénoms de naissance" value={data.identity.firstNamesBirth} source={data.identitySources?.firstNamesBirth} />
                        <FieldButton label="Date naissance" value={data.identity.dob} source={data.identitySources?.dob} champ="dob" />
                        <FieldButton label="Sexe" value={data.identity.sex} source={data.identitySources?.sex} champ="sex" />
                        <FieldButton label="Lieu naissance (INSEE)" value={data.identity.birthCityCode} source={data.identitySources?.birthCityCode} champ="birthCityCode" />
                        <FieldButton label="Téléphone" value={data.identity.phone} source={data.identitySources?.phone} champ="phone" />
                        <FieldButton label="Email" value={data.identity.email} source={data.identitySources?.email} champ="email" />
                      </div>

                      {data.identityCoherence && (
                        <div style={{ marginTop: 8, padding: 8, borderRadius: 10, background: data.identityCoherence.isCoherent ? "#e9fbf2" : "#fff0f0", border: `1px solid ${data.identityCoherence.isCoherent ? "#bff1d6" : "#ffb3b3"}` }}>
                          <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 6, color: data.identityCoherence.isCoherent ? "#0b6f3b" : "#c0392b" }}>
                            {data.identityCoherence.isCoherent ? "✓ Identité cohérente (Patient ↔ Ordonnance)" : "⚠ Incohérence détectée (Patient ↔ Ordonnance)"}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {[
                              { label: "Nom", ...data.identityCoherence.nom },
                              { label: "Prénom", ...data.identityCoherence.prenom },
                              { label: "DDN", ...data.identityCoherence.dob },
                              { label: "NIR", ...data.identityCoherence.nir },
                            ].map(({ label, a, b, match }) => (
                              <div key={label} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 10, background: match ? "transparent" : "#ffe0e0", borderRadius: 6, padding: match ? "1px 0" : "3px 6px" }}>
                                <span style={{ fontWeight: 800, color: "var(--muted)", minWidth: 44 }}>{label}</span>
                                <span style={{ color: "#1b3b52", fontWeight: 700 }}>{a || "—"}</span>
                                {!match && <span style={{ color: "#c0392b", fontWeight: 900 }}>≠</span>}
                                {match && a && b && <span style={{ color: "var(--ok)", fontWeight: 900 }}>✓</span>}
                                {b && b !== a && <span style={{ color: "#1b3b52", fontWeight: 700, opacity: 0.7 }}>/ {b}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {codeLuOuvert && (
                        <CodeLuINS
                          lecture={data.insReading}
                          titre={titreSourceCode(data.insReading?.source)}
                          onClose={() => setCodeLuOuvert(false)}
                        />
                      )}

                      <div className="sb-editToggleRow">
                        <button className="sb-btnMini sb-blue" onClick={() => setEditOpen(!editOpen)}>Éditer NIR / Lieu / N° dossier</button>
                      </div>
                      <div className={`sb-editPanel ${editOpen ? "sb-show" : ""}`}>
                        {/*
                          LES TROIS CHAMPS PORTENT LEUR TITRE.

                          Retour du 08/09 : « sur le Web, peux tu ajouter les
                          titre des champs. On devine par rapport a l'ordre
                          affiche au-dessus mais rappelle les. »

                          Sa capture montre trois cases contenant
                          « 277010115400329 », « 01154 » et « 1234567890 »,
                          sans un mot autour. Le placeholder ne suffisait pas,
                          et c'est structurel : il DISPARAIT des qu'il y a une
                          valeur, c'est-a-dire exactement au moment ou l'on
                          relit ce qu'on a tape. Le titre, lui, reste.

                          Il reste aussi un `placeholder`, mais reduit a ce
                          qu'un titre ne dit pas : le gabarit attendu.
                        */}
                        <div className="sb-editGrid">
                          <label className="sb-editChamp">
                            <span className="sb-editLabel">NIR</span>
                            <input value={inNir} onChange={(e) => setInNir(e.target.value)} placeholder="15 chiffres" />
                          </label>
                          <label className="sb-editChamp">
                            <span className="sb-editLabel">Lieu de naissance</span>
                            <input value={inBirthCity} onChange={(e) => setInBirthCity(e.target.value)} placeholder="Code INSEE" />
                          </label>
                          <label className="sb-editChamp">
                            <span className="sb-editLabel">N° dossier</span>
                            <input value={inDossierBarcode} onChange={(e) => setInDossierBarcode(e.target.value)} placeholder="N° dossier" />
                          </label>
                          <button className="sb-btnMini sb-ok" onClick={async () => {
                            const dossierVal = (inDossierBarcode || "").trim();
                            setData((prev) => ({ ...prev, identity: { ...prev.identity, nir: inNir, ins: nirSansCle(inNir), birthCityCode: inBirthCity }, dossierBarcode: dossierVal }));
                            if (dossierVal && selectedAppointmentId) {
                              try {
                                await updateDoc(doc(firebase.db, "appointements", selectedAppointmentId), { barcode: dossierVal });
                                // B1 : synchroniser le cache local des RDV (saisie manuelle)
                                setAppointments((prev) => prev.map((a) => (a.id === selectedAppointmentId ? { ...a, barcode: dossierVal } : a)));
                                toast("N° dossier enregistré !");
                              } catch (e) { toast("Erreur sauvegarde dossier"); }
                            } else {
                              toast("Identité mise à jour (local)");
                            }
                          }}>OK</button>
                        </div>
                      </div>
                    </div>

                    {/* Prescripteur + RPPS */}
                    {data.prescripteurInfo?.nom && (
                      <div className="sb-card">
                        <h3><span className="sb-icon">&#x1F469;&#x200D;&#x2695;&#xFE0F;</span> Prescripteur</h3>
                        <div className="sb-grid3">
                          <FieldButton label="Nom" value={data.prescripteurInfo.nom} />
                          {data.prescripteurInfo.prenom && <FieldButton label="Prénom" value={data.prescripteurInfo.prenom} />}
                          {data.prescripteurInfo.specialite && <FieldButton label="Spécialité" value={data.prescripteurInfo.specialite} />}
                          {data.prescripteurInfo.rpps && <FieldButton label="RPPS" value={data.prescripteurInfo.rpps} />}
                          {data.prescripteurInfo.adeliAM && <FieldButton label="ADELI/AM" value={data.prescripteurInfo.adeliAM} />}
                        </div>
                        {/* Code SIL prescripteur (table de correspondance) */}
                        {data.prescripteurInfo?.rpps && (
                          <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 10, background: "#f3f7fb", border: "1px solid var(--line)" }}>
                            <div className="sb-k" style={{ marginBottom: 4 }}>Code SIL labo (RPPS {data.prescripteurInfo.rpps})</div>
                            {editPrescripteurSil ? (
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <input
                                  value={inPrescripteurSilCode}
                                  onChange={(e) => setInPrescripteurSilCode(e.target.value)}
                                  placeholder="Code SIL prescripteur..."
                                  onKeyDown={(e) => { if (e.key === "Enter") savePrescripteurSilCode(); if (e.key === "Escape") setEditPrescripteurSil(false); }}
                                  style={{ flex: 1, border: "1px solid var(--blue)", borderRadius: 7, padding: "4px 8px", fontSize: 12, fontWeight: 700, fontFamily: "monospace" }}
                                  autoFocus
                                />
                                <button className="sb-btnMini sb-ok" onClick={savePrescripteurSilCode}>OK</button>
                                <button className="sb-btnMini" onClick={() => setEditPrescripteurSil(false)}>✕</button>
                              </div>
                            ) : (
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span
                                  style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 13, color: prescripteurSilCode ? "#0b1b2a" : "var(--muted)", flex: 1, cursor: "pointer" }}
                                  onClick={() => prescripteurSilCode && copyToClipboard(prescripteurSilCode)}
                                  title={prescripteurSilCode ? "Cliquer pour copier" : ""}
                                >
                                  {prescripteurSilCode || <em style={{ fontWeight: 400, fontSize: 11 }}>Non renseigné</em>}
                                </span>
                                <button
                                  className="sb-btnMini sb-blue"
                                  onClick={() => { setInPrescripteurSilCode(prescripteurSilCode || ""); setEditPrescripteurSil(true); }}
                                  title="Modifier le code SIL"
                                >✎ Modifier</button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* La carte de mutuelle, mise en forme (18/09). Elle
                        s'ouvre toute seule quand on clique le document
                        « Mutuelle » a droite : voir l'effet sur `selectedDocId`.
                        Sans `mutuelleResume`, on ne rend pas rien : on dit OU
                        EN EST la lecture, faite par le back-office depuis le
                        01/10 (voir l'effet `fautLancerLecture`). */}
                    <div ref={mutuelleRef}>
                      <CarteMutuelle
                        resume={data.mutuelleResume}
                        ouvert={mutuelleOuverte}
                        onBasculer={() => setMutuelleOuverte((o) => !o)}
                        onCopier={copyToClipboard}
                        etat={mutuelleLecture}
                        onRelire={relireMutuelle}
                      />
                    </div>

                    {/* ─── 🧪 Tubes — DÉSACTIVÉ POUR LE MOMENT ───
                    <div className="sb-card">
                      <h3><span className="sb-icon">&#x1F9EA;</span> Tubes</h3>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: "var(--muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.3px" }}>Tubes nécessaires</div>
                        {data.tubes.length > 0 ? (
                          <div className="sb-tubeWrap">
                            {data.tubes.map((t, i) => (
                              <div className="sb-tubeChip" key={i}>
                                <span className={`sb-tubeDot sb-${t.color}`} />{t.label} &times;{t.count}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="sb-pill">{data.hasAnalysisResult ? "Aucun tube détecté" : "Pas d'analyse IA"}</div>
                        )}
                      </div>
                      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: "var(--muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.3px" }}>Tubes reconnus (photo)</div>
                        {data.tubesReconnus ? (
                          <>
                            {data.tubesScanMeta?.confirmedBy && (
                              <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 4 }}>
                                Par : {data.tubesScanMeta.confirmedBy}
                                {data.tubesScanMeta.confirmedAt && ` · ${data.tubesScanMeta.confirmedAt.slice(0,16).replace("T"," ")}`}
                              </div>
                            )}
                            <div className="sb-tubeWrap">
                              {data.tubesReconnus.map((t, i) => {
                                const needed = data.tubes.filter(n => n.color === t.color).reduce((s, n) => s + n.count, 0);
                                const isExtra = t.count > needed;
                                return (
                                  <div className="sb-tubeChip" key={i} style={isExtra ? { borderColor: "#f2a933", background: "#fff4dd" } : {}}>
                                    <span className={`sb-tubeDot sb-${t.color}`} />{t.label} &times;{t.count}
                                    {isExtra && <span style={{ fontSize: 9, color: "#8a5b00", fontWeight: 800 }}>+</span>}
                                  </div>
                                );
                              })}
                            </div>
                            {data.tubes.map((t, i) => {
                              const recognized = data.tubesReconnus.filter(r => r.color === t.color).reduce((s, r) => s + r.count, 0);
                              if (recognized < t.count) {
                                return (
                                  <div key={i} className="sb-tubeChip" style={{ marginTop: 4, borderColor: "#ffb3b3", background: "#fff0f0" }}>
                                    <span className={`sb-tubeDot sb-${t.color}`} />
                                    <span style={{ color: "#c0392b", fontWeight: 800 }}>Manquant: {t.label} &times;{t.count - recognized}</span>
                                  </div>
                                );
                              }
                              return null;
                            })}
                            {(() => {
                              const neededByColor = {};
                              data.tubes.forEach(t => { neededByColor[t.color] = (neededByColor[t.color] || 0) + t.count; });
                              const reconByColor = {};
                              data.tubesReconnus.forEach(t => { reconByColor[t.color] = (reconByColor[t.color] || 0) + t.count; });
                              const isOk = Object.entries(neededByColor).every(([c, n]) => (reconByColor[c] || 0) >= n)
                                && Object.entries(reconByColor).every(([c, n]) => (neededByColor[c] || 0) >= n);
                              return (
                                <div style={{ marginTop: 6, padding: "5px 8px", borderRadius: 8, background: isOk ? "#e9fbf2" : "#fff0f0", border: `1px solid ${isOk ? "#bff1d6" : "#ffb3b3"}`, fontSize: 11, fontWeight: 800, color: isOk ? "#0b6f3b" : "#c0392b" }}>
                                  {isOk ? "✓ Tubes cohérents" : "⚠ Incohérence tubes"}
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <div className="sb-pill" style={{ opacity: 0.6, fontStyle: "italic" }}>Non analysé</div>
                        )}
                      </div>
                    </div>
                    ─── Fin bloc Tubes désactivé ─── */}

                    {/* Commentary */}
                    {data.commentary && (
                      <div className="sb-card">
                        <h3><span className="sb-icon">&#x1F4AC;</span> Commentaire (clic = copie)</h3>
                        {/* Demande de Nicolas du 05/10/2026 : le mot du
                            preleveur se recopie au clic, comme les champs du
                            bloc Identite. Copie du texte entier, sauts de
                            ligne compris. */}
                        <div
                          className="sb-copieTexte"
                          onClick={() => copyToClipboard(data.commentary)}
                          title="Cliquer pour copier le commentaire"
                        >
                          {data.commentary}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT COL */}
                  <div className="sb-col">
                    <div className="sb-split">
                      {/* Documents */}
                      <div className="sb-card" >
                        <h3><span className="sb-icon">&#x1F4C4;</span> Documents — type + envoi SGL</h3>
                        {/* Retour du 04/09 : le bandeau de qualité est retiré.
                            « Ne mets pas en place des batteries de contrôle,
                            fais en sorte d'avoir une compression correcte dès
                            la prise de photo. » Le bon endroit pour régler la
                            qualité est l'appareil, pas un avertissement au
                            secrétariat — c'est fait côté tablette (cible
                            700-800 ko). La MESURE reste faite et enregistrée
                            côté serveur, sans rien afficher : elle ne coûte
                            rien à l'écran et c'est elle qui nous dira si les
                            photos se dégradent à nouveau. */}
                        {/* Le resume du dossier, sur sa propre ligne, avant
                            tous les documents, et ALIGNE A DROITE
                            (Nicolas, 18/09 18h18 : « Les boutons afficher et
                            telecharger le resume PDF doivent etre aligne a
                            droite de l'ecran »). */}
                        <div className="sb-row sb-resumeLigne">
                          <button
                            className="sb-btnMini sb-ok"
                            disabled={resumeEnCours || !selectedAppointmentId}
                            onClick={afficherResumePdf}
                          >
                            {resumeEnCours ? "Préparation…" : "Afficher le résumé PDF"}
                          </button>
                          <button
                            className="sb-btnMini sb-blue"
                            disabled={resumeEnCours || !selectedAppointmentId}
                            onClick={telechargerResumePdf}
                          >
                            Télécharger le résumé PDF
                          </button>
                        </div>
                        <div className="sb-row" style={{ marginBottom: 8 }}>
                          <button className="sb-btnMini sb-blue sb-inactive" onClick={sglNotAvailable}>
                            Rafraîchir statuts SGL<span className="sb-comingSoon">indisponible</span>
                          </button>
                        </div>
                        {data.documents.length > 0 ? (
                          <div className="sb-preview">
                            <div className="sb-prevTop">
                              <div className="sb-prevLeft">
                                <div className="sb-title">{selectedDoc?.type || "—"}</div>
                              </div>
                              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                {data.documents.length > 1 && data.documents.map((docItem) => (
                                  <button key={docItem.id} className={`sb-btnMini2 ${docItem.id === selectedDocId ? "sb-ok" : ""}`} onClick={() => { marquerTravail(); setSelectedDocId(docItem.id); }}>
                                    {docItem.type}
                                  </button>
                                ))}
                                {selectedDoc && (
                                  <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, border: "1px solid", background: selectedDoc.sglStatus === "SENT" ? "#eef6ff" : selectedDoc.sglStatus === "INTEGRATED" ? "#e9fbf2" : "#fff4dd", borderColor: selectedDoc.sglStatus === "SENT" ? "#cfe6ff" : selectedDoc.sglStatus === "INTEGRATED" ? "#bff1d6" : "#ffe2a1", color: selectedDoc.sglStatus === "SENT" ? "#1d5fa1" : selectedDoc.sglStatus === "INTEGRATED" ? "#0b6f3b" : "#8a5b00" }}>
                                    SGL: {docStatusLabel(selectedDoc.sglStatus)}
                                  </span>
                                )}
                                {/*
                                  DEUX BOUTONS, UN PAR FORMAT.

                                  Retour du 08/09 : « si tu peux ajouter
                                  Telecharger en PDF disponible pour chaque
                                  document ce serait parfait car le SIL ne
                                  semble accepter que les PDF. »

                                  « Chaque document » est deja servi par les
                                  pastilles de gauche : elles changent le
                                  document affiche, et ces deux boutons
                                  agissent sur celui qui est affiche. Le JPG
                                  est conserve — c'est l'original, et il reste
                                  le meilleur format pour relire une photo.
                                */}
                                {selectedDoc && (
                                  <button className="sb-btnMini sb-blue" onClick={() => downloadDocument(selectedDoc.storagePath, selectedDoc)}>
                                    Télécharger JPG
                                  </button>
                                )}
                                {selectedDoc && (
                                  <button
                                    className="sb-btnMini sb-blue"
                                    disabled={pdfEnCours}
                                    onClick={() => downloadDocumentPdf(selectedDoc.storagePath, selectedDoc)}
                                  >
                                    {pdfEnCours ? "Conversion…" : "Télécharger PDF"}
                                  </button>
                                )}
                              </div>
                            </div>
                            <div
                              className="sb-canvas"
                              onWheel={(e) => {
                                e.preventDefault();
                                setInlineZoom((z) => {
                                  const delta = e.deltaY > 0 ? -0.15 : 0.15;
                                  const next = Math.min(5, Math.max(1, +(z + delta).toFixed(2)));
                                  if (next === 1) setInlinePos({ x: 0, y: 0 });
                                  return next;
                                });
                              }}
                            >
                              {docPreviewLoading ? (
                                <div className="sb-loading"><div className="sb-spinner" /><span style={{ fontSize: 11 }}>Chargement...</span></div>
                              ) : docPreviewUrl ? (
                                <>
                                  <img
                                    src={docPreviewUrl}
                                    alt={selectedDoc?.type || "Document"}
                                    title={inlineZoom > 1 ? "Glissez pour naviguer" : "Molette pour zoomer"}
                                    onError={() => { if (!apercuRelanceRef.current) { apercuRelanceRef.current = true; relancerApercuUneFois(); } }}
                                    style={{
                                      transform: `scale(${inlineZoom}) translate(${inlinePos.x / inlineZoom}px, ${inlinePos.y / inlineZoom}px)`,
                                    }}
                                    onClick={() => {
                                      if (inlineZoom <= 1) {
                                        setZoomScale(1); setZoomPos({ x: 0, y: 0 }); setZoomOpen(true);
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      if (inlineZoom <= 1) return;
                                      e.preventDefault();
                                      const img = e.currentTarget;
                                      img.classList.add("sb-dragging");
                                      inlineDragRef.current = { startX: e.clientX - inlinePos.x, startY: e.clientY - inlinePos.y };
                                      const onMove = (ev) => {
                                        if (!inlineDragRef.current) return;
                                        setInlinePos({ x: ev.clientX - inlineDragRef.current.startX, y: ev.clientY - inlineDragRef.current.startY });
                                      };
                                      const onUp = () => {
                                        inlineDragRef.current = null;
                                        img.classList.remove("sb-dragging");
                                        window.removeEventListener("mousemove", onMove);
                                        window.removeEventListener("mouseup", onUp);
                                      };
                                      window.addEventListener("mousemove", onMove);
                                      window.addEventListener("mouseup", onUp);
                                    }}
                                  />
                                  {inlineZoom > 1 ? (
                                    <div className="sb-zoomFloat">
                                      <button className="sb-zoomFloatBtn" onClick={() => setInlineZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}>−</button>
                                      <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, minWidth: 40, textAlign: "center" }}>{Math.round(inlineZoom * 100)}%</span>
                                      <button className="sb-zoomFloatBtn" onClick={() => setInlineZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))}>+</button>
                                      <button className="sb-zoomFloatBtn" onClick={() => { setInlineZoom(1); setInlinePos({ x: 0, y: 0 }); }}>↺</button>
                                      <button className="sb-zoomFloatBtn" onClick={() => { setZoomScale(1); setZoomPos({ x: 0, y: 0 }); setZoomOpen(true); }}>⛶</button>
                                    </div>
                                  ) : (
                                    <div className="sb-zoomHint" onClick={() => { setZoomScale(1); setZoomPos({ x: 0, y: 0 }); setZoomOpen(true); }}>🔍</div>
                                  )}
                                </>
                              ) : selectedDoc?.storagePath ? (
                                /*
                                  LE CHEMIN EXISTE, L'URL PAS ENCORE.

                                  La tablette pose le chemin du fichier ~8 s
                                  apres la creation du dossier, et l'objet
                                  encore un peu plus tard. Entre les deux,
                                  `getDownloadURL` repond
                                  `storage/object-not-found` et l'effet
                                  reprend : on le DIT, plutot que d'afficher
                                  « Sélectionnez un document » — la
                                  secretaire vient de le selectionner.
                                */
                                <span>Chargement de la photo…</span>
                              ) : (
                                <span>Sélectionnez un document pour l'aperçu</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          /*
                            LE DOSSIER EXISTE, SES PHOTOS NON ENCORE.

                            La tablette cree le dossier au debut de la prise en
                            charge et n'y ecrit les chemins qu'a la fin (~8 s).
                            Entre les deux, l'ecran disait « Aucun document
                            joint » — c'est-a-dire « ce patient n'a rien
                            apporte », alors qu'on attendait ses photos. On
                            nomme l'attente.
                          */
                          <div className="sb-pill">{photosEnRoute() ? "Photos en cours d'envoi depuis la tablette…" : "Aucun document joint"}</div>
                        )}
                      </div>

                      {/* ─── ✅ Analyses — vérification — DÉSACTIVÉ POUR LE MOMENT ───
                      <div className="sb-card" >
                        <h3><span className="sb-icon">&#x2705;</span> Analyses — vérification</h3>
                        {data.analyses.length > 0 ? (
                          <>
                            <div className="sb-analysisTop">
                              <div className="sb-copyWide">
                                <div style={{ minWidth: 100 }}>
                                  <div className="sb-label">Codes SIL ({data.analyses.length})</div>
                                  <div className="sb-value">{data.analyses.map((a) => a.codeSIL || a.codeNABM).filter(Boolean).join(" ")}</div>
                                </div>
                                <button className="sb-btnMini sb-ok" onClick={() => copyToClipboard(data.analyses.map((a) => a.codeSIL || a.codeNABM).filter(Boolean).join(" "))}>
                                  Copier
                                </button>
                              </div>
                              <button className="sb-btnMini sb-ok" disabled onClick={notAvailable}>Tout vérifier</button>
                              <button className="sb-btnMini" disabled onClick={notAvailable}>Retirer tout</button>
                            </div>
                            <div className="sb-analysisList">
                              {verified.length > 0 && (
                                <>
                                  <div className="sb-sectionTitle">Vérifiées</div>
                                  {verified.map((a) => (
                                    <div className="sb-aRow" key={a.id}>
                                      <div>
                                        <div className="sb-aTitle">{a.code} — {a.label}</div>
                                        <div className="sb-aSub">
                                          {a.tubeRecommande && <span className="sb-tag">{a.tubeRecommande} {a.nombreTubes > 0 ? `x${a.nombreTubes}` : ""}</span>}
                                          {a.aJeun && <span className="sb-tag sb-nv">À jeun</span>}
                                          {a.nonRembourse && <span className="sb-tag sb-nv">NR{a.prixPatient ? ` ${a.prixPatient}€` : ""}</span>}
                                          {a.codeNABM && <span className="sb-tag sb-ai">NABM {a.codeNABM}</span>}
                                          <span className="sb-tag sb-v">Vérifiée</span>
                                        </div>
                                      </div>
                                      <div className="sb-who">
                                        <div className="sb-v">{a.verifiedBy && a.verifiedBy !== "none" ? a.verifiedBy : "—"}</div>
                                        <div className="sb-t">{a.verifiedAt ? a.verifiedAt.slice(0,16).replace("T"," ") : "—"}</div>
                                      </div>
                                      <div className="sb-aActions">
                                        <button className="sb-btnMini2 sb-ok" disabled>Vérifier</button>
                                        <button className="sb-btnMini2 sb-remove" disabled>Retirer</button>
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )}
                              <div className="sb-sectionTitle">Non vérifiées</div>
                              {notVerified.length > 0 ? (
                                notVerified.map((a) => (
                                  <div className="sb-aRow" key={a.id}>
                                    <div>
                                      <div className="sb-aTitle">{a.code} — {a.label}</div>
                                      <div className="sb-aSub">
                                        {a.tubeRecommande && <span className="sb-tag">{a.tubeRecommande} {a.nombreTubes > 0 ? `x${a.nombreTubes}` : ""}</span>}
                                        {a.aJeun && <span className="sb-tag sb-nv">À jeun</span>}
                                        {a.nonRembourse && <span className="sb-tag sb-nv">NR{a.prixPatient ? ` ${a.prixPatient}€` : ""}</span>}
                                        {a.codeNABM && <span className="sb-tag sb-ai">NABM {a.codeNABM}</span>}
                                        <span className="sb-tag sb-nv">Non vérifiée</span>
                                      </div>
                                    </div>
                                    <div className="sb-aActions">
                                      <button className="sb-btnMini2 sb-ok" disabled>Vérifier</button>
                                      <button className="sb-btnMini2 sb-remove" disabled>Retirer</button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="sb-pill sb-ok">Toutes vérifiées</div>
                              )}
                            </div>
                            <div style={{ marginTop: 6, fontSize: 10, color: "var(--muted)", fontStyle: "italic" }}>
                              Vérification des analyses — bientôt disponible
                            </div>
                          </>
                        ) : (
                          <div className="sb-pill">{data.hasAnalysisResult ? "Aucune analyse détectée" : "Pas d'analyse IA pour ce dossier"}</div>
                        )}
                      </div>
                      ─── Fin bloc Analyses désactivé ─── */}
                    </div>
                  </div>
                </div>

                {/* FOOTER */}
                <div className="sb-footer">
                  {/* Retours du 04/09. Trois choses ici :
                      — « Dossier déjà traité » n'est plus un mur : on reclique
                        et le dossier retrouve son statut initial ;
                      — le statut « À vérifier », pour passer la main à une
                        collègue plus expérimentée sans sortir du produit ;
                      — le mot qu'on lui laisse, sous les deux boutons. */}
                  <button
                    className="sb-btnDossierCree"
                    onClick={() =>
                      statutCourant() === STATUT_TRAITE
                        ? poserStatut(STATUT_NOUVEAU)
                        : handleDossierCree()
                    }
                    title={
                      statutCourant() === STATUT_TRAITE
                        ? "Cliquer pour remettre le dossier à son statut initial"
                        : "Marquer ce dossier comme traité"
                    }
                  >
                    {statutCourant() === STATUT_TRAITE
                      ? "Dossier déjà traité — annuler"
                      : "Dossier créé"}
                  </button>
                  <button
                    className={`sb-btnAVerifier ${statutCourant() === STATUT_A_VERIFIER ? "sb-actif" : ""}`}
                    onClick={() =>
                      poserStatut(
                        statutCourant() === STATUT_A_VERIFIER ? STATUT_NOUVEAU : STATUT_A_VERIFIER
                      )
                    }
                    title="Demander à une collègue de regarder ce dossier"
                  >
                    {statutCourant() === STATUT_A_VERIFIER ? "À vérifier — annuler" : "À vérifier"}
                  </button>
                </div>
                <div className="sb-footerNote">
                  {/* Retour du 04/09 (17h41) : « lorsqu'on écrit, on ne sait
                      pas comment valider ». Le champ s'enregistrait en perdant
                      le focus — ce qui marche, mais ne se devine pas. Un
                      bouton OK le dit. Ctrl+Entrée fait la même chose pour
                      celles qui ne quittent pas le clavier ; la sortie du
                      champ enregistre toujours, pour qu'un message ne se
                      perde pas parce qu'on a oublié de cliquer. */}
                  <div className="sb-commentaireLigne">
                    <textarea
                      className="sb-commentaire"
                      value={commentaire}
                      onChange={(e) => setCommentaire(e.target.value)}
                      onBlur={enregistrerCommentaire}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          enregistrerCommentaire();
                        }
                      }}
                      placeholder="Message pour les collègues sur ce dossier"
                    />
                    <button
                      className="sb-btnOkCommentaire"
                      onClick={enregistrerCommentaire}
                      disabled={commentaire.trim() === commentaireEnregistre.trim()}
                      title="Enregistrer le message (Ctrl+Entrée)"
                    >
                      OK
                    </button>
                  </div>
                  <div className="sb-commentaireBas">
                    {dossierCourant()?.commentaireSecretaireAuteur && (
                      <div className="sb-commentaireMeta">
                        Dernier message laissé par{" "}
                        {nomLisible(dossierCourant().commentaireSecretaireAuteur)}
                      </div>
                    )}
                    {/* L'historique des statuts, dans une bulle : on veut
                        savoir qui a posé quoi et quand, sans que cela occupe
                        l'écran en permanence. */}
                    {!!dossierCourant()?.historiqueStatuts?.length && (
                      <div className="sb-histoAncre">
                        <button
                          className="sb-btnMini"
                          onClick={() => setHistoOuvert((o) => !o)}
                        >
                          Historique des statuts ({dossierCourant().historiqueStatuts.length})
                        </button>
                        {histoOuvert && (
                          <div className="sb-histoBulle">
                            <div className="sb-histoTitre">Historique des statuts</div>
                            {[...dossierCourant().historiqueStatuts]
                              .reverse()
                              .map((h, i) => (
                                <div className="sb-histoLigne" key={`${h.le}-${i}`}>
                                  <b>{libelleStatut(h.statut)}</b> — {nomLisible(h.par)}
                                  <div className="sb-histoQuand">
                                    {h.le ? new Date(h.le).toLocaleString("fr-FR") : "—"}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          </Filet>

          {/* Changement de dossier alors que « Dossier cree » n a pas ete clique :
              on demande confirmation plutot que de perdre le travail. */}
          {dossierEnAttente && (
            <div className="sb-confirmOverlay" onClick={() => setDossierEnAttente(null)}>
              <div className="sb-confirmBox" onClick={(e) => e.stopPropagation()}>
                <div className="sb-confirmTitle">Ce dossier n'a pas été marqué créé</div>
                <div className="sb-confirmText">
                  Vous avez travaillé sur ce dossier sans cliquer sur « Dossier créé ».
                  Voulez-vous vraiment ouvrir un autre dossier ?
                </div>
                <div className="sb-confirmActions">
                  <button
                    className="sb-btnMini sb-ok"
                    onClick={async () => {
                      const suivant = dossierEnAttente;
                      await poserStatut(STATUT_TRAITE);
                      handleSelectAppointment(suivant);
                    }}
                  >
                    Marquer créé, puis continuer
                  </button>
                  <button
                    className="sb-btnMini"
                    onClick={() => {
                      const suivant = dossierEnAttente;
                      handleSelectAppointment(suivant);
                    }}
                  >
                    Changer sans marquer
                  </button>
                  <button className="sb-btnMini sb-blue" onClick={() => setDossierEnAttente(null)}>
                    Rester sur ce dossier
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* « Dossier créé » cliqué sans avoir touché au dossier (retour du
              04/09). On ne bloque pas, on demande — le geste est peut-être
              volontaire, mais il ne doit pas être machinal. */}
          {creationADemander && (
            <div className="sb-confirmOverlay" onClick={() => setCreationADemander(false)}>
              <div className="sb-confirmBox" onClick={(e) => e.stopPropagation()}>
                <div className="sb-confirmTitle">Confirmer que le dossier a été créé ?</div>
                <div className="sb-confirmText">
                  Vous n'avez rien consulté ni modifié sur ce dossier. Voulez-vous
                  quand même le marquer comme traité ?
                </div>
                <div className="sb-confirmActions">
                  <button
                    className="sb-btnMini sb-ok"
                    onClick={async () => {
                      setCreationADemander(false);
                      await poserStatut(STATUT_TRAITE);
                    }}
                  >
                    Oui, le dossier est créé
                  </button>
                  <button className="sb-btnMini sb-blue" onClick={() => setCreationADemander(false)}>
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quitter l'écran alors qu'un dossier est en travail. La question
              est la nôtre, avec les mêmes trois réponses que pour un
              changement de dossier — et non la boîte grise du navigateur, que
              Nicolas n'a pas vue passer. */}
          {sortiePage && (
            <div className="sb-confirmOverlay">
              <div className="sb-confirmBox" onClick={(e) => e.stopPropagation()}>
                <div className="sb-confirmTitle">Ce dossier n'a pas été marqué créé</div>
                <div className="sb-confirmText">
                  Vous avez travaillé sur ce dossier sans cliquer sur « Dossier créé ».
                  Voulez-vous vraiment quitter cette page ?
                </div>
                <div className="sb-confirmActions">
                  <button
                    className="sb-btnMini sb-ok"
                    onClick={async () => {
                      const { repondre } = sortiePage;
                      setSortiePage(null);
                      await poserStatut(STATUT_TRAITE);
                      repondre(true);
                    }}
                  >
                    Marquer créé, puis quitter
                  </button>
                  <button
                    className="sb-btnMini"
                    onClick={() => {
                      const { repondre } = sortiePage;
                      setSortiePage(null);
                      repondre(true);
                    }}
                  >
                    Quitter sans marquer
                  </button>
                  <button
                    className="sb-btnMini sb-blue"
                    onClick={() => {
                      const { repondre } = sortiePage;
                      setSortiePage(null);
                      repondre(false);
                    }}
                  >
                    Rester sur ce dossier
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quitter l ecran par le menu de gauche : meme question. */}
          <Prompt
            when={dossierNonConclu()}
            message="Ce dossier n'a pas été marqué « Dossier créé ». Voulez-vous vraiment quitter cette page ?"
          />

          {/* Toast */}
          <div className={`sb-toast ${toastVisible ? "sb-show" : ""}`}>{toastMsg}</div>
        </div>

        {/* Zoom overlay */}
        {zoomOpen && docPreviewUrl && (
          <div
            className="sb-zoomOverlay"
            onClick={() => setZoomOpen(false)}
            onWheel={(e) => {
              e.preventDefault();
              setZoomScale((s) => {
                const delta = e.deltaY > 0 ? -0.15 : 0.15;
                return Math.min(6, Math.max(0.3, +(s + delta).toFixed(2)));
              });
            }}
          >
            <div className="sb-zoomToolbar" onClick={(e) => e.stopPropagation()}>
              <button className="sb-zoomBtn" onClick={() => setZoomScale((s) => Math.max(0.3, +(s - 0.25).toFixed(2)))}>−</button>
              <span className="sb-zoomLevel">{Math.round(zoomScale * 100)}%</span>
              <button className="sb-zoomBtn" onClick={() => setZoomScale((s) => Math.min(6, +(s + 0.25).toFixed(2)))}>+</button>
              <button className="sb-zoomBtn" onClick={() => { setZoomScale(1); setZoomPos({ x: 0, y: 0 }); }}>↺</button>
              <button className="sb-zoomBtn" onClick={() => setZoomOpen(false)}>✕</button>
            </div>
            <div
              className={`sb-zoomImgWrap${dragRef.current ? " sb-grabbing" : ""}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => {
                e.preventDefault();
                dragRef.current = { startX: e.clientX - zoomPos.x, startY: e.clientY - zoomPos.y };
                const wrap = e.currentTarget;
                wrap.classList.add("sb-grabbing");
                const onMove = (ev) => {
                  if (!dragRef.current) return;
                  setZoomPos({ x: ev.clientX - dragRef.current.startX, y: ev.clientY - dragRef.current.startY });
                };
                const onUp = () => {
                  dragRef.current = null;
                  wrap.classList.remove("sb-grabbing");
                  window.removeEventListener("mousemove", onMove);
                  window.removeEventListener("mouseup", onUp);
                };
                window.addEventListener("mousemove", onMove);
                window.addEventListener("mouseup", onUp);
              }}
              style={{
                transform: `translate(${zoomPos.x}px, ${zoomPos.y}px)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={docPreviewUrl}
                alt="Zoom"
                style={{
                  maxHeight: "85vh",
                  maxWidth: "90vw",
                  width: "auto",
                  height: "auto",
                  transform: `scale(${zoomScale})`,
                  transformOrigin: "center center",
                  transition: "transform 0.1s ease",
                }}
              />
            </div>
          </div>
        )}

        {/* Ce qui a changé dans cette section, en bas de page pour ne pas
            couper le travail. Le bloc disparaît de lui-même au bout de trente
            jours : une nouveauté permanente n'est plus une nouveauté. */}
        <BlocNouveaute section="secretaryBackoffice" />
      </div>
    </>
  );
}
